import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { useNavigate, useSearchParams } from "react-router-dom";
import { trackUxEvent } from "../analytics/analytics";
import {
  ScreenerSearchRequest,
  getProfessionalScreenerFilterOptions,
  searchProfessionalScreener,
} from "../api/professionalScreenerApi";
import StockSearchSuggestInput from "../components/common/StockSearchSuggestInput";
import type { StockSearchSuggestion } from "../api/stockSearchApi";
import "../styles/policy-intelligence-ui.css";

type RowMap = Record<string, any>;

type ScreenerPageProps = {
  onGoStocks?: (category: string, ticker?: string) => void;
};

type FilterState = ScreenerSearchRequest & {
  styleTags?: string[];
};

type PresetKey = "ALL" | "VALUE" | "QUALITY" | "ETF_MACRO" | "FLOW" | "EVENT";
type SignalTone = "positive" | "negative" | "warning" | "neutral";
type ScreenerView = "GENERAL" | "VALUE_FINANCIAL" | "PERFORMANCE" | "FLOW" | "TECHNICAL" | "EVENT";
type AdvancedFilterSection = "BASIC" | "FUNDAMENTAL" | "FLOW_TECH" | "EVENT_RISK";

function screenerTableColumnCount(view: ScreenerView): number {
  if (view === "GENERAL") return 10;
  if (view === "VALUE_FINANCIAL") return 12;
  if (view === "PERFORMANCE") return 9;
  if (view === "FLOW") return 9;
  if (view === "TECHNICAL") return 9;
  return 9;
}

const ADVANCED_FILTER_SECTIONS: Array<{
  key: AdvancedFilterSection;
  label: string;
  helper: string;
}> = [
  { key: "BASIC", label: "기본", helper: "시장 · 업종 · 시총 · 거래" },
  { key: "FUNDAMENTAL", label: "가치·재무", helper: "PER · PBR · ROE · ROIC · FCF" },
  { key: "FLOW_TECH", label: "수급·기술", helper: "외국인 · 기관 · 거래배율 · 변동성" },
  { key: "EVENT_RISK", label: "이벤트·위험", helper: "공시 · 52주 · 배당 · 위험 제외" },
];

const UI = {
  bg: "#f4f6fa",
  card: "#ffffff",
  soft: "#f8f9fc",
  line: "#dde3ee",
  lineSoft: "#ebeff6",
  text: "#111c31",
  sub: "#4a5972",
  muted: "#66758b",
  navy: "#101b31",
  blue: "#4155d9",
  red: "#d34861",
  green: "#068369",
  amber: "#ae6a25",
  slate: "#617188",
};

const DEFAULT_FILTERS: FilterState = {
  market: "ALL",
  keyword: "",
  minTradingValue: null,
  minMarketCap: null,
  minScore: null,
  excludeRiskAvoid: true,
  sortBy: "score",
  sortDirection: "DESC",
  page: 0,
  size: 50,
};

const STYLE_OPTIONS = [
  { value: "QUALITY_GROWTH", label: "우량 성장" },
  { value: "VALUE_CASHFLOW", label: "저평가·현금흐름" },
  { value: "MACRO_SECTOR_ALIGNED", label: "매크로 정렬" },
  { value: "DISCLOSURE_EVENT", label: "공시 이벤트" },
  { value: "WATCHLIST", label: "관찰 후보" },
  { value: "RISK_AVOID", label: "위험 회피" },
];

const SORT_OPTIONS = [
  { value: "score", label: "종합 점수" },
  { value: "priceChange", label: "등락률" },
  { value: "tradingValue", label: "거래대금" },
  { value: "tradingValueRatio", label: "거래대금 배율" },
  { value: "volatility20d", label: "20일 변동성" },
  { value: "atr14d", label: "ATR(14)" },
  { value: "beta60d", label: "Beta 60일" },
  { value: "macroShock", label: "Macro Shock Score" },
  { value: "marketCap", label: "시가총액" },
  { value: "per", label: "PER 낮은순" },
  { value: "pbr", label: "PBR 낮은순" },
  { value: "pegRatio", label: "PEG 낮은순" },
  { value: "profitPricing", label: "실적 대비 가격 매력" },
  { value: "profitPriceGap", label: "실적-가격 괴리" },
  { value: "operatingIncomeGrowth", label: "영업이익 증가율" },
  { value: "availableMarketCapChange", label: "가용기간 시총 변화" },
  { value: "revenueCagr3y", label: "매출 성장률" },
  { value: "roicWaccSpread", label: "ROIC-WACC" },
  { value: "sectorChange", label: "ETF 섹터 등락" },
  { value: "sectorFlow", label: "ETF 섹터 흐름" },
  { value: "macroAlignment", label: "매크로 정렬" },
  { value: "disclosureImpact", label: "공시 영향" },
  { value: "sharpe252d", label: "샤프 252일 높은순" },
  { value: "sharpe60d", label: "샤프 60일 높은순" },
  { value: "maxDrawdown252d", label: "최대낙폭 방어순" },
];

const PRESETS: Array<{
  key: PresetKey;
  label: string;
  helper: string;
  patch: Partial<FilterState>;
}> = [
  {
    key: "ALL",
    label: "전체",
    helper: "조건 직접 설정",
    patch: { ...DEFAULT_FILTERS },
  },
  {
    key: "VALUE",
    label: "저평가",
    helper: "PER·PBR·PEG",
    patch: {
      ...DEFAULT_FILTERS,
      minTradingValue: 1_000_000_000,
      minPer: 0,
      maxPer: 15,
      minPbr: 0,
      maxPbr: 1.5,
      maxPegRatio: 1.5,
      sortBy: "pbr",
      sortDirection: "ASC",
    },
  },
  {
    key: "QUALITY",
    label: "우량 성장",
    helper: "재무·성장",
    patch: {
      ...DEFAULT_FILTERS,
      minScore: 65,
      minRoicWaccSpread: 3,
      minOperatingMargin: 8,
      minRoe: 8,
      minRevenueCagr3y: 3,
      styleTags: ["QUALITY_GROWTH"],
      sortBy: "roicWaccSpread",
      sortDirection: "DESC",
    },
  },
  {
    key: "ETF_MACRO",
    label: "ETF 섹터 강세",
    helper: "섹터·매크로",
    patch: {
      ...DEFAULT_FILTERS,
      minEtfSectorChangePct: 1,
      minSectorFlowScore: 55,
      minMacroAlignmentScore: 50,
      styleTags: ["MACRO_SECTOR_ALIGNED"],
      sortBy: "sectorChange",
      sortDirection: "DESC",
    },
  },
  {
    key: "FLOW",
    label: "수급 정렬",
    helper: "외국인·기관",
    patch: {
      ...DEFAULT_FILTERS,
      minSectorFlowScore: 60,
      minMacroAlignmentScore: 55,
      foreignNetBuy20dPositive: true,
      sortBy: "sectorFlow",
      sortDirection: "DESC",
    },
  },
  {
    key: "EVENT",
    label: "공시 이벤트",
    helper: "공시 영향",
    patch: {
      ...DEFAULT_FILTERS,
      minDisclosureImpactScore: 50,
      styleTags: ["DISCLOSURE_EVENT"],
      sortBy: "disclosureImpact",
      sortDirection: "DESC",
    },
  },
];

const METRIC_HELP: Record<string, string> = {
  score:
    "재무 품질, 밸류에이션, 수급, 변동성, 매크로 환경을 종합한 스크리너 점수입니다. 높을수록 조건상 우호적입니다.",
  buyScore:
    "신규 진입 관점의 점수입니다. 재무·수급·밸류가 좋아도 변동성, Beta, Macro Shock이 높으면 감점됩니다.",
  holdScore:
    "이미 보유 중인 종목을 계속 들고 갈 수 있는지 보는 점수입니다. 재무 체력과 수급 훼손 여부를 함께 반영합니다.",
  sellRiskScore:
    "매도 또는 비중 축소가 필요한 위험 점수입니다. 높을수록 변동성, 수급 이탈, 재무 부담, 시장 충격이 큽니다.",
  macroRiskScore:
    "시장 전체 위험이 해당 종목 판단에 주는 부담입니다. 환율, VIX, 신용 스트레스, 외국인 이탈, 파생 압력 등을 반영합니다.",
  hv20: "HV(20)는 최근 20거래일 일간 수익률의 흔들림을 연율화한 변동성입니다. 높을수록 단기 급등락 가능성이 큽니다.",
  atr14:
    "ATR(14)는 최근 14거래일 평균 가격 변동폭입니다. 현재 DB 구조에서는 고가·저가 대신 종가와 등락률 기반 근사값을 함께 사용합니다.",
  beta60:
    "Beta 60일은 시장 대비 민감도입니다. 1.0은 시장과 비슷하고, 1.2는 시장보다 약 20% 더 민감하게 움직이는 경향을 뜻합니다.",
  macroShock:
    "Macro Shock Score는 현재 매크로 충격이 이 종목에 얼마나 부담이 되는지 보는 점수입니다. Beta, 변동성, 환율, VIX, 외국인 이탈, 파생 압력 등을 결합합니다.",
  tradingValueRatio:
    "거래대금 배율은 오늘 거래대금이 최근 20일 평균 대비 몇 배인지 보는 지표입니다. 상승·하락 움직임의 신뢰도를 판단할 때 사용합니다.",
  marketCapBucket:
    "시가총액 규모 구간입니다. 같은 변동성이라도 초대형주와 소형주의 위험 해석이 달라지므로 보조 기준으로 봅니다.",
  fairValue:
    "가치평가 결과는 신뢰 수준에 따라 참고값, 중앙 추정가, 대표 적정가로 구분합니다. 근거가 부족한 값은 참고용으로만 표시합니다.",
  fairValueUpside:
    "현재가와의 괴리는 충분한 평가 근거가 확보된 경우에만 판단에 사용합니다. 참고값만 있는 경우에는 고평가·저평가 판단을 보류합니다.",
  fairValueSource:
    "S-RIM과 Justified PBR은 같은 자본수익성 관점의 교차검증으로 묶어 중복 계산을 피합니다. 서로 다른 평가 근거가 충분히 일치할 때 중앙 추정가를 제공합니다.",
  rimFairPrice:
    "RIM(잔여이익모형)은 지배주주 귀속 자본과 순이익을 우선 사용해 현재 자기자본에 향후 초과이익의 현재가치를 더합니다. 관련 이력이 부족하면 연결 재무 기준으로 계산하되 서로 다른 회계 기준을 한 계산에 섞지 않습니다.",
  rimUpside:
    "RIM 적정가를 현재가와 비교한 괴리입니다. 양수이면 현재가가 모델 적정가보다 낮고, 음수이면 현재가가 적정가보다 높다는 뜻입니다.",
  rimRoe:
    "정상 ROE는 선택된 RIM 회계 기준의 최근 연간 순이익을 직전 연도 자기자본으로 나눈 역사적 ROE들의 중앙값입니다. 지배주주 귀속 자료가 충분하면 전 구간을 owner 기준으로 사용합니다.",
  rimCostOfEquity:
    "요구수익률은 종목의 시장 민감도를 우선 반영하고, 자료가 부족하면 동종업계 값을 보조적으로 사용합니다. 근거가 약한 추정치는 참고값으로만 사용합니다.",
  rimRetention:
    "재투자율은 확인 가능한 배당과 자본 변화를 바탕으로 추정합니다. 자본거래 영향을 충분히 구분하기 어려운 경우에는 참고값으로만 사용합니다.",
  per: "PER은 주가를 주당순이익으로 나눈 값입니다. 낮을수록 이익 대비 주가가 싸 보일 수 있지만, 성장성·일회성 이익 여부도 함께 봐야 합니다.",
  pbr: "PBR은 주가를 주당순자산으로 나눈 값입니다. 낮을수록 자산 대비 저평가 가능성이 있지만, ROE와 산업 특성을 함께 봐야 합니다.",
  peg: "PEG는 PER을 이익 성장률로 나눈 값입니다. 성장 대비 밸류에이션 부담을 보는 지표로, 낮을수록 성장 대비 가격 부담이 낮다는 의미입니다.",
  psr: "PSR은 주가를 주당매출로 나눈 값입니다. 이익이 불안정한 성장주를 볼 때 보조적으로 사용합니다.",
  evOp: "EV/OP는 기업가치(EV)를 영업이익으로 나눈 값입니다. 영업이익 대비 기업 전체 가격 부담을 보는 밸류 지표입니다.",
  profitPricing:
    "최근 연간 영업이익 YoY와 약 1년 가격 반응을 같은 기간축으로 비교하고 EV/OP·FCF 수익률·PER을 함께 반영한 보조 점수입니다. 1년 가격 이력이 부족하면 점수를 산출하지 않습니다.",
  profitPriceGap:
    "최근 연간 영업이익 증가율에서 약 1년 시가총액 변화율(불가 시 주가 변화율)을 뺀 값입니다. 서로 다른 장기 누적기간과 연간 실적을 직접 비교하지 않습니다.",
  profitRepricing:
    "실적 성장 대비 가격 배수 변화입니다. 음수이면 실적 증가보다 가격 상승이 느려 밸류에이션 부담이 완화됐고, 양수이면 가격 배수가 확장된 상태입니다.",
  operatingIncomeGrowth:
    "최근 연간 사업보고서 기준 영업이익의 전년 대비 증가율입니다. 직전 또는 현재 영업이익이 적자이면 실적-가격 비교 점수는 제한합니다.",
  availableMarketCapChange:
    "최신 거래일 기준 약 1년 전부터 현재까지의 시가총액 변화율입니다. 과거 시가총액이 없으면 같은 기간 주가 변화를 사용하며, 기준기간이 250일 미만이면 실적-가격 비교를 제한합니다.",
  fcfYield:
    "FCF 수익률은 시가총액 대비 잉여현금흐름 비율입니다. 높고 양수이면 현금 창출력이 상대적으로 좋다는 의미입니다.",
  roicWacc:
    "ROIC-WACC는 투하자본수익률에서 자본비용을 뺀 값입니다. 양수이면 자본비용보다 높은 수익을 내고 있다는 뜻입니다.",
  operatingMargin:
    "영업이익률은 매출에서 영업이익이 차지하는 비율입니다. 높을수록 본업 수익성이 좋습니다.",
  roe: "ROE는 자기자본이익률입니다. 주주자본 대비 이익 창출력을 보여줍니다.",
  revenueCagr:
    "매출 CAGR은 최근 3년 매출의 연평균 성장률입니다. 구조적 성장 여부를 보는 지표입니다.",
  debtToEquity:
    "부채비율은 자기자본 대비 부채 규모입니다. 높을수록 금리 상승기와 경기 둔화기에 부담이 커질 수 있습니다.",
  currentRatio:
    "유동비율은 단기 부채를 갚을 수 있는 단기 자산 여력을 보는 안정성 지표입니다.",
  interestCoverage:
    "이자보상배율은 영업이익으로 이자비용을 얼마나 감당할 수 있는지 보는 지표입니다. 낮을수록 재무 위험이 큽니다.",
  etfSector:
    "ETF 섹터는 종목이 연결된 대표 섹터 흐름입니다. 개별 종목 판단과 별도로 섹터 자금·가격 방향을 참고합니다.",
  etfChange:
    "ETF 등락률은 연결 섹터 ETF의 최근 등락입니다. 종목의 업종 환경을 빠르게 확인하는 지표입니다.",
  etfFlowScore:
    "ETF 흐름점수는 외국인·기관 등 섹터 ETF 수급을 점수화한 값입니다. 높을수록 섹터 자금 흐름이 우호적입니다.",
  macroAlignment:
    "매크로 정렬 점수는 현재 매크로 환경과 해당 섹터·종목 방향이 얼마나 맞는지 보는 지표입니다.",
  disclosureImpact:
    "공시 영향은 최근 공시나 이벤트가 종목 판단에 미치는 강도를 점수화한 값입니다.",
  foreign5d:
    "외국인 5일 순매수는 최근 5거래일 외국인 순매수 흐름입니다. 단기 수급 방향을 빠르게 확인합니다.",
  institution5d:
    "기관 5일 순매수는 최근 5거래일 기관 순매수 흐름입니다. 외국인 수급과 함께 단기 주도 주체를 봅니다.",
  foreign20d:
    "외국인 20일 수급은 중기 외국인 누적 수급 흐름입니다. 단기보다 추세 확인에 가깝습니다.",
  institution20d:
    "기관 20일 수급은 중기 기관 누적 수급 흐름입니다. 연기금·투신 등 기관성 자금 흐름을 참고합니다.",
  pricePosition52w:
    "52주 위치는 현재가가 최근 52주 고가와 저가 사이에서 어느 위치인지 보는 값입니다. 데이터가 부족하면 숫자 대신 상태로 표시합니다.",
  sharpe60d:
    "샤프 단기는 최근 60거래일 일별 수익률에서 연 3% 무위험수익률을 차감하고 변동성으로 나눈 연환산 위험조정 성과입니다.",
  sharpe252d:
    "샤프 1년은 최근 최대 252거래일의 위험조정 성과입니다. 200개 이상의 유효 관측치가 있을 때만 계산됩니다.",
  maxDrawdown252d:
    "최근 최대 252거래일 동안 직전 고점에서 가장 크게 하락했던 비율입니다. 0에 가까울수록 낙폭 방어가 좋습니다.",
  annualizedReturn:
    "일별 평균 수익률을 252거래일 기준으로 연환산한 값입니다. 실제 향후 수익률을 보장하지 않습니다.",
  annualizedVolatility:
    "일별 수익률 표준편차를 252거래일 기준으로 연환산한 값입니다. 높을수록 가격 흔들림이 큽니다.",
  sharpeDataStatus:
    "VALID_252D는 252일 지표 계산 가능, PARTIAL_60D는 60일 지표만 가능, INSUFFICIENT_PRICE_HISTORY는 가격 이력이 부족한 상태입니다.",
};

function HelpLabel({ label, help }: { label: React.ReactNode; help?: string }) {
  if (!help) return <>{label}</>;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.35,
        verticalAlign: "middle",
        minWidth: 0,
      }}
    >
      <Box
        component="span"
        sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
      >
        {label}
      </Box>
      <Tooltip
        title={help}
        arrow
        enterTouchDelay={0}
        leaveTouchDelay={5000}
        componentsProps={{
          tooltip: {
            sx: {
              maxWidth: 320,
              bgcolor: "#0f172a",
              color: "#f8fafc",
              fontSize: 12.5,
              lineHeight: 1.55,
              fontWeight: 700,
              borderRadius: 1.5,
              px: 1.25,
              py: 1,
              boxShadow: "0 14px 32px rgba(15,23,42,.28)",
            },
          },
          arrow: { sx: { color: "#0f172a" } },
        }}
      >
        <Box
          component="span"
          aria-label="지표 설명"
          role="button"
          tabIndex={0}
          onClick={(event) => event.stopPropagation()}
          sx={{
            width: 16,
            height: 16,
            borderRadius: "50%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: UI.muted,
            border: `1px solid ${UI.line}`,
            bgcolor: "#fff",
            flex: "0 0 auto",
            cursor: "help",
            "&:hover": {
              color: UI.navy,
              borderColor: UI.navy,
              bgcolor: "#eef6ff",
            },
          }}
        >
          <HelpOutlineRoundedIcon sx={{ fontSize: 12 }} />
        </Box>
      </Tooltip>
    </Box>
  );
}


function sanitizeMoneyInput(value: string): string {
  return String(value ?? "").replace(/[^0-9]/g, "");
}

function formatMoneyDraft(value: string): string {
  const digits = sanitizeMoneyInput(value);
  if (!digits) return "";
  const normalized = digits.replace(/^0+(?=\d)/, "");
  return normalized.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function sanitizeDecimalInput(value: string, maxDecimalDigits = 1): string {
  const raw = String(value ?? "").replace(/,/g, "");
  let cleaned = raw.replace(/[^0-9.]/g, "");
  const firstDot = cleaned.indexOf(".");

  if (firstDot >= 0) {
    const integerPart = cleaned.slice(0, firstDot).replace(/\./g, "");
    const decimalPart = cleaned
      .slice(firstDot + 1)
      .replace(/\./g, "")
      .slice(0, maxDecimalDigits);
    return `${integerPart}.${decimalPart}`;
  }

  return cleaned;
}

function sanitizeIntegerInput(value: string): string {
  return String(value ?? "").replace(/[^0-9]/g, "");
}

function normalizeNumericText(
  value: string,
  mode: NumberFieldMode = "decimal",
): string {
  if (mode === "money") return sanitizeMoneyInput(value);
  if (mode === "integer") return sanitizeIntegerInput(value);
  return sanitizeDecimalInput(value, 1);
}

function numberOrNull(
  value: string,
  mode: NumberFieldMode = "decimal",
): number | null {
  const raw = normalizeNumericText(value, mode);
  if (!raw || raw === ".") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

type NumberFieldMode = "decimal" | "integer" | "money";

function formatNumberInputValue(
  value: any,
  mode: NumberFieldMode = "decimal",
): string {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return "";

  if (mode === "money") {
    return formatMoneyDraft(String(Math.trunc(n)));
  }

  if (mode === "integer") {
    return String(Math.trunc(n));
  }

  const rounded = Math.round(n * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function formatDraftByMode(
  value: string,
  mode: NumberFieldMode = "decimal",
): string {
  if (mode === "money") return formatMoneyDraft(value);
  if (mode === "integer") return sanitizeIntegerInput(value);
  return sanitizeDecimalInput(value, 1);
}

function normalizeTickerParam(value: any): string {
  const raw = String(value ?? "").trim();
  const matched =
    raw.match(/^A?(\d{6})$/)?.[1] ?? raw.match(/\b(\d{6})\b/)?.[1];
  return matched || "";
}

function get(row: RowMap, key: string): any {
  return row[key] ?? row[key.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`)];
}

function formatNumber(value: any, digits = 0): string {
  if (value === null || value === undefined || value === "") return "-";
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  return n.toLocaleString("ko-KR", {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  });
}

function formatWon(value: any): string {
  if (value === null || value === undefined || value === "") return "-";
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1_0000_0000_0000)
    return `${sign}${formatNumber(abs / 1_0000_0000_0000, 1)}조`;
  if (abs >= 1_0000_0000)
    return `${sign}${formatNumber(abs / 1_0000_0000, 0)}억`;
  if (abs >= 1_0000) return `${sign}${formatNumber(abs / 1_0000, 0)}만`;
  return `${sign}${formatNumber(abs)}`;
}

function formatPct(value: any, digits = 2): string {
  if (value === null || value === undefined || value === "") return "-";
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  return `${formatNumber(n, digits)}%`;
}

function formatMultiple(value: any, digits = 2): string {
  if (value === null || value === undefined || value === "") return "-";
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  return `${formatNumber(n, digits)}배`;
}




function formatAtr14dCompact(row: RowMap): string {
  const price = Number(get(row, "atr14dPrice"));
  const pct = get(row, "atr14dPct");
  const pctText = formatPct(pct, 1);
  if (Number.isFinite(price) && price > 0)
    return `${formatNumber(price, 0)}원 · ${pctText}`;
  return pctText;
}

function formatPricePosition52w(row: RowMap): string {
  const status = String(get(row, "pricePosition52wStatus") ?? "").toUpperCase();
  const observations = Number(get(row, "price52wObservationCount"));
  const value = get(row, "pricePosition52wPct");
  if (status && status !== "OK") {
    if (status === "INSUFFICIENT_PRICE_HISTORY") {
      return Number.isFinite(observations) && observations > 0
        ? `데이터 부족(${observations}일)`
        : "데이터 부족";
    }
    if (status === "FLAT_52W_RANGE") return "변동폭 없음";
    return "확인중";
  }
  return formatPct(value, 2);
}



function formatSignedPct(value: any, digits = 2): string {
  if (value === null || value === undefined || value === "") return "-";
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  return `${n > 0 ? "+" : ""}${formatNumber(n, digits)}%`;
}

function toneColor(tone: SignalTone): string {
  if (tone === "positive") return UI.green;
  if (tone === "negative") return UI.red;
  if (tone === "warning") return UI.amber;
  return UI.slate;
}

function metricTone(value: any): SignalTone {
  if (value === null || value === undefined || value === "") return "neutral";
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return "neutral";
  return n > 0 ? "positive" : "negative";
}












type ScreenerFairValueView = {
  fairPrice: number | null;
  upsidePct: number | null;
  state: string;
  label: string;
  tone: SignalTone;
  sourceLabel: string;
  modelCount: number | null;
  eligibleFamilyCount: number | null;
  availableFamilyCount: number | null;
  consensusStatus: string;
  confidence: string;
  reasonCodes: string[];
  representativeFairValue: number | null;
  representativeAvailable: boolean;
  centralAvailable: boolean;
  rimFairPrice: number | null;
  justifiedPbr: number | null;
  justifiedPbrFairPrice: number | null;
  modelSpreadPct: number | null;
  valuationLevel: string;
  referenceOnly: boolean;
};

function finiteNumberOrNull(value: any): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function positiveNumberOrNull(value: any): number | null {
  const parsed = finiteNumberOrNull(value);
  return parsed !== null && parsed > 0 ? parsed : null;
}

function firstFiniteNumber(...values: any[]): number | null {
  for (const value of values) {
    const parsed = finiteNumberOrNull(value);
    if (parsed !== null) return parsed;
  }
  return null;
}

function firstPositiveNumber(...values: any[]): number | null {
  for (const value of values) {
    const parsed = positiveNumberOrNull(value);
    if (parsed !== null) return parsed;
  }
  return null;
}

function fairValueStateLabel(value: any): string {
  const state = String(value ?? "").toUpperCase();
  if (["CENTRAL_DEEP_UNDERVALUED", "DEEP_UNDERVALUED"].includes(state)) return "중앙 추정가보다 크게 낮음";
  if (["CENTRAL_UNDERVALUED", "QUALITY_UNDERVALUED"].includes(state)) return "중앙 추정가보다 낮음";
  if (["CENTRAL_BELOW_ESTIMATE", "BELOW_FAIR_VALUE"].includes(state)) return "중앙 추정가 이하";
  if (["CENTRAL_NEAR_ESTIMATE", "FAIR_VALUE", "FAIR"].includes(state)) return "중앙 추정가 부근";
  if (["CENTRAL_ABOVE_ESTIMATE", "OVERVALUED", "STRONG_OVERVALUED"].includes(state)) return "중앙 추정가 상회";
  if (state === "CENTRAL_UNAVAILABLE" || state === "LIMITED_DATA" || state === "LIMITED") return "중앙 추정가 제한";
  return state || "계산 제한";
}

function fairValueStateTone(value: any, upsidePct: number | null): SignalTone {
  const state = String(value ?? "").toUpperCase();
  if (["CENTRAL_DEEP_UNDERVALUED", "CENTRAL_UNDERVALUED", "CENTRAL_BELOW_ESTIMATE", "DEEP_UNDERVALUED", "QUALITY_UNDERVALUED", "BELOW_FAIR_VALUE"].includes(state)) return "positive";
  if (["CENTRAL_ABOVE_ESTIMATE", "OVERVALUED", "STRONG_OVERVALUED"].includes(state)) return "negative";
  if (["CENTRAL_UNAVAILABLE", "WITHHELD", "LIMITED_DATA", "LIMITED"].includes(state)) return "warning";
  if (upsidePct !== null && upsidePct >= 15) return "positive";
  if (upsidePct !== null && upsidePct <= -10) return "negative";
  return "neutral";
}

function resolveScreenerFairValue(row: RowMap): ScreenerFairValueView {
  const consensusStatus = String(get(row, "valuationConsensusStatus") ?? get(row, "consensusStatus") ?? "WITHHELD").toUpperCase();
  const centralStatus = String(get(row, "valuationCentralEstimateStatus") ?? get(row, "centralEstimateStatus") ?? "UNAVAILABLE").toUpperCase();
  const valuationLevel = String(get(row, "valuationLevel") ?? "REFERENCE").toUpperCase();
  const centralEstimate = firstPositiveNumber(get(row, "valuationCentralEstimate"), get(row, "centralEstimate"));
  const referenceMedian = firstPositiveNumber(get(row, "valuationReferenceMedian"), get(row, "referenceMedian"));
  const fairPrice = centralStatus === "AVAILABLE" ? centralEstimate : referenceMedian;
  const representativeFairValue = firstPositiveNumber(get(row, "representativeFairValue"), get(row, "consensusFairValue"));
  const representativeAvailable = consensusStatus === "AVAILABLE" && representativeFairValue !== null;
  const centralAvailable = centralStatus === "AVAILABLE" && centralEstimate !== null;
  const referenceOnly = !centralAvailable && referenceMedian !== null;
  const rimFairPrice = firstPositiveNumber(get(row, "srimFairPrice"), get(row, "rimFairPrice"));
  const currentPrice = firstPositiveNumber(get(row, "closePrice"));
  const explicitUpside = centralAvailable
    ? firstFiniteNumber(get(row, "valuationCentralGapPct"), get(row, "centralGapPct"))
    : firstFiniteNumber(get(row, "valuationReferenceGapPct"), get(row, "referenceGapPct"));
  const upsidePct = explicitUpside !== null ? explicitUpside
    : fairPrice !== null && currentPrice !== null ? (fairPrice / currentPrice - 1) * 100 : null;
  const state = String((centralAvailable ? get(row, "valuationCentralState") ?? get(row, "centralValuationState") : "CENTRAL_UNAVAILABLE") ?? "").toUpperCase();
  const modelCountRaw = firstFiniteNumber(get(row, "modelCount"), get(row, "valuationModelCount"));
  const modelCount = modelCountRaw !== null && modelCountRaw >= 0 ? Math.round(modelCountRaw) : null;
  const eligibleFamilyRaw = firstFiniteNumber(get(row, "eligibleFamilyCount"));
  const availableFamilyRaw = firstFiniteNumber(get(row, "availableFamilyCount"));
  const eligibleFamilyCount = eligibleFamilyRaw !== null ? Math.round(eligibleFamilyRaw) : null;
  const availableFamilyCount = availableFamilyRaw !== null ? Math.round(availableFamilyRaw) : null;
  const confidence = String(centralAvailable ? (get(row, "valuationCentralEstimateConfidence") ?? "LOW") : "LOW").toUpperCase();
  const sourceLabel = representativeAvailable ? "여러 평가 근거가 충분히 일치"
    : centralAvailable ? "서로 다른 평가 근거의 중앙값"
    : referenceOnly ? "분석 참고용 · 가격 판단 보류" : "산출 제한";
  const label = centralAvailable ? fairValueStateLabel(state)
    : referenceOnly ? "참고값 · 고평가/저평가 판단 보류" : "가치 판단 제한";
  const justifiedPbrFairPrice = firstPositiveNumber(get(row, "justifiedPbrFairPrice"));
  const justifiedPbr = firstPositiveNumber(get(row, "justifiedPbr"));
  const modelSpreadPct = firstFiniteNumber(get(row, "valuationFamilyDispersionPct"), get(row, "modelDispersionPct"));
  const reasonCodes = Array.isArray(get(row, "valuationReasonCodes")) ? get(row, "valuationReasonCodes") : [];
  return {
    fairPrice, representativeFairValue, representativeAvailable, centralAvailable, rimFairPrice,
    upsidePct, state, label, tone: centralAvailable ? fairValueStateTone(state, upsidePct) : "warning",
    sourceLabel, modelCount, eligibleFamilyCount, availableFamilyCount, consensusStatus, confidence,
    justifiedPbrFairPrice, justifiedPbr, modelSpreadPct, reasonCodes, valuationLevel, referenceOnly,
  };
}



function formatPriceWon(value: any): string {
  if (value === null || value === undefined || value === "") return "-";
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "-";
  return `${formatNumber(n, 0)}원`;
}



function scoreColor(score: any): string {
  const n = Number(score);
  if (Number.isFinite(n) && n >= 85) return UI.green;
  if (Number.isFinite(n) && n >= 75) return UI.blue;
  if (Number.isFinite(n) && n >= 65) return UI.amber;
  return UI.slate;
}






function drawdownTone(value: any): SignalTone {
  const n = Number(value);
  if (!Number.isFinite(n)) return "neutral";
  if (n <= -30) return "negative";
  if (n <= -20) return "warning";
  return "positive";
}




function formatSharpe(value: any): string {
  const n = Number(value);
  return Number.isFinite(n) ? formatNumber(n, 2) : "-";
}




function styleLabel(code?: string): string {
  return STYLE_OPTIONS.find((x) => x.value === code)?.label || code || "-";
}



function deriveSignal(row: RowMap): {
  label: string;
  tone: SignalTone;
  helper: string;
} {
  const diagnosisLabel = String(
    get(row, "diagnosisDecisionLabel") || "",
  ).trim();
  const diagnosisTone = String(get(row, "diagnosisTone") || "").trim();
  const diagnosisSummary = String(get(row, "diagnosisSummary") || "").trim();
  if (diagnosisLabel) {
    const tone: SignalTone =
      diagnosisTone === "positive"
        ? "positive"
        : diagnosisTone === "negative"
          ? "negative"
          : diagnosisTone === "warning"
            ? "warning"
            : "neutral";
    return {
      label: diagnosisLabel,
      tone,
      helper: diagnosisSummary || "산식 기반 판정입니다.",
    };
  }

  const style = String(get(row, "screenerStyleTag") || "");
  const warning = String(get(row, "screenerWarningText") || "");
  const score = Number(get(row, "professionalScreenerScore"));
  const sector = Number(get(row, "sectorEtfFlowScore"));
  const macro = Number(get(row, "macroAlignmentScore"));
  const foreign = Number(get(row, "foreignNetBuy20dToTradingValuePct"));
  const institution = Number(get(row, "institutionNetBuy20dToTradingValuePct"));
  const tradingValue = Number(get(row, "tradingValue"));

  if (style === "RISK_AVOID" || /위험|주의|과대|적자/.test(warning))
    return {
      label: "위험 점검",
      tone: "warning",
      helper: warning || "위험 조건 확인 필요",
    };
  if (
    Number.isFinite(tradingValue) &&
    tradingValue > 0 &&
    tradingValue < 1_000_000_000
  )
    return { label: "거래 부족", tone: "warning", helper: "거래대금 부족" };
  if (
    Number.isFinite(sector) &&
    Number.isFinite(macro) &&
    sector >= 60 &&
    macro >= 55
  )
    return {
      label: "정렬 우수",
      tone: "positive",
      helper: "섹터와 매크로가 같은 방향",
    };
  if (
    Number.isFinite(foreign) &&
    Number.isFinite(institution) &&
    foreign > 0 &&
    institution > 0
  )
    return {
      label: "쌍끌이 수급",
      tone: "positive",
      helper: "외국인·기관 동시 순매수",
    };
  if (Number.isFinite(score) && score >= 75)
    return {
      label: "우량 관찰",
      tone: "positive",
      helper: "점수 기준 우수 후보",
    };
  if (
    Number.isFinite(foreign) &&
    foreign < 0 &&
    Number.isFinite(institution) &&
    institution < 0
  )
    return {
      label: "동반 이탈",
      tone: "negative",
      helper: "외국인·기관 동시 이탈",
    };
  return { label: "관찰 필요", tone: "neutral", helper: "추가 확인 필요" };
}

function fieldActive(
  filters: FilterState,
  key: keyof FilterState,
  expected?: any,
): boolean {
  const value = filters[key];
  if (expected !== undefined) return value === expected;
  return (
    value !== null && value !== undefined && value !== "" && value !== false
  );
}

function compactFilterLabel(key: string, value: any): string {
  if (key === "market") return `시장 ${value}`;
  if (key === "keyword") return `검색 ${value}`;
  if (key === "sectorCode") return `ETF 섹터 ${value}`;
  if (key === "minTradingValue") return `거래대금 ${formatWon(value)}+`;
  if (key === "minMarketCap") return `시총 ${formatWon(value)}+`;
  if (key === "minScore") return `점수 ${value}+`;
  if (key === "minPer") return `PER ${value}+`;
  if (key === "maxPer") return `PER ${value} 이하`;
  if (key === "minPbr") return `PBR ${value}+`;
  if (key === "maxPbr") return `PBR ${value} 이하`;
  if (key === "minPegRatio") return `PEG ${value}+`;
  if (key === "maxPegRatio") return `PEG ${value} 이하`;
  if (key === "minEtfSectorChangePct") return `ETF 등락 ${value}%+`;
  if (key === "maxEtfSectorChangePct") return `ETF 등락 ${value}% 이하`;
  if (key === "minSectorFlowScore") return `섹터흐름 ${value}+`;
  if (key === "minMacroAlignmentScore") return `매크로 ${value}+`;
  if (key === "minDisclosureImpactScore") return `공시 ${value}+`;
  if (key === "foreignNetBuy20dPositive") return "외국인 20일 순매수";
  if (key === "institutionNetBuy20dPositive") return "기관 20일 순매수";
  if (key === "excludeRiskAvoid") return "위험 회피 제외";
  return `${key}: ${value}`;
}

function activeFilterChips(
  filters: FilterState,
): Array<{ key: string; label: string }> {
  const keys: Array<keyof FilterState> = [
    "keyword",
    "market",
    "sectorCode",
    "minTradingValue",
    "minMarketCap",
    "minScore",
    "minPer",
    "maxPer",
    "minPbr",
    "maxPbr",
    "minPegRatio",
    "maxPegRatio",
    "maxPriceToSales",
    "maxEvToOperatingIncome",
    "minRoicWaccSpread",
    "minOperatingMargin",
    "minRoe",
    "minRevenueCagr3y",
    "maxDebtToEquity",
    "minPriceChangePct",
    "maxPriceChangePct",
    "minTradingValueRatio20d",
    "minHistoricalVolatility20d",
    "maxHistoricalVolatility20d",
    "minAtr14dPct",
    "maxAtr14dPct",
    "maxStockBeta60d",
    "maxMacroShockScore",
    "foreignNetBuy5dPositive",
    "institutionNetBuy5dPositive",
    "minPricePosition52w",
    "maxPricePosition52w",
    "minEtfSectorChangePct",
    "maxEtfSectorChangePct",
    "minSectorFlowScore",
    "minMacroAlignmentScore",
    "minDividendYield",
    "minFcfYield",
    "minDisclosureImpactScore",
    "foreignNetBuy20dPositive",
    "institutionNetBuy20dPositive",
    "excludeRiskAvoid",
  ];
  const chips: Array<{ key: string; label: string }> = [];
  keys.forEach((key) => {
    const value = filters[key];
    if (key === "market" && (!value || value === "ALL")) return;
    if (!fieldActive(filters, key)) return;
    chips.push({
      key: String(key),
      label: compactFilterLabel(String(key), value),
    });
  });
  (filters.styleTags || []).forEach((tag) =>
    chips.push({ key: `style-${tag}`, label: styleLabel(tag) }),
  );
  return chips;
}

function buildTickerLandingFilters(keyword: string, size: number): FilterState {
  return {
    ...DEFAULT_FILTERS,
    keyword: String(keyword || "").trim(),
    minTradingValue: null,
    minScore: null,
    excludeRiskAvoid: false,
    page: 0,
    size: size || DEFAULT_FILTERS.size,
  };
}

function normalizeSectorOptions(
  data: any,
): Array<{ code: string; label: string; helper?: string }> {
  const rows = Array.isArray(data?.sectorOptions)
    ? data.sectorOptions
    : Array.isArray(data)
      ? data
      : [];
  return rows
    .map((row: any) => {
      const code = String(row?.sectorCode ?? row?.sector_code ?? "").trim();
      const name = String(row?.sectorName ?? row?.sector_name ?? code).trim();
      const etf = String(
        row?.representativeEtfTicker ?? row?.representative_etf_ticker ?? "",
      ).trim();
      const stockCount = row?.stockCount ?? row?.stock_count;
      if (!code) return null;
      return {
        code,
        label: name || code,
        helper: [etf, stockCount != null ? `${stockCount}종목` : ""]
          .filter(Boolean)
          .join(" · "),
      };
    })
    .filter(Boolean) as Array<{ code: string; label: string; helper?: string }>;
}

function MiniMetric({
  label,
  value,
  tone,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  tone?: SignalTone;
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{ color: UI.muted, fontSize: 11, fontWeight: 800, lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: tone ? toneColor(tone) : UI.text,
          fontSize: 13,
          fontWeight: 900,
          mt: 0.2,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function NumberField({
  label,
  value,
  onChange,
  placeholder,
  mode = "decimal",
}: {
  label: React.ReactNode;
  value: any;
  onChange: (value: number | null) => void;
  placeholder?: string;
  mode?: NumberFieldMode;
}) {
  const [draft, setDraft] = useState(() => formatNumberInputValue(value, mode));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(formatNumberInputValue(value, mode));
  }, [focused, mode, value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextDraft = formatDraftByMode(event.target.value, mode);
    setDraft(nextDraft);
    onChange(numberOrNull(nextDraft, mode));
  };

  const handleBlur = () => {
    setFocused(false);
    setDraft(formatNumberInputValue(numberOrNull(draft, mode), mode));
  };

  return (
    <TextField
      label={label}
      size="small"
      value={draft}
      placeholder={placeholder}
      onFocus={() => setFocused(true)}
      onBlur={handleBlur}
      onChange={handleChange}
      fullWidth
      inputProps={{
        inputMode:
          mode === "money" || mode === "integer" ? "numeric" : "decimal",
        pattern: mode === "decimal" ? "[0-9]*[.]?[0-9]?" : "[0-9,]*",
      }}
      sx={{
        "& .MuiOutlinedInput-root": { minHeight: 36, borderRadius: 1.1, bgcolor: "#fff", fontSize: 11.5 },
        "& .MuiInputLabel-root": { fontSize: 11.5 },
        "& .MuiInputBase-input": { py: 0.9, fontVariantNumeric: "tabular-nums" },
      }}
    />
  );
}




function SelectableChip({
  selected,
  label,
  onClick,
}: {
  selected: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <Chip
      clickable
      label={label}
      onClick={onClick}
      sx={{
        height: 32,
        borderRadius: 1.8,
        fontWeight: 850,
        color: selected ? UI.navy : UI.sub,
        bgcolor: selected ? alpha(UI.navy, 0.09) : "#fff",
        border: `1px solid ${selected ? alpha(UI.navy, 0.42) : UI.line}`,
        boxShadow: selected
          ? `inset 0 0 0 1px ${alpha(UI.navy, 0.08)}`
          : "none",
        "& .MuiChip-label": { px: 1.15 },
        "&:hover": {
          bgcolor: selected ? alpha(UI.navy, 0.13) : "#f4f7fb",
          borderColor: selected ? UI.navy : "#bcc8d8",
        },
      }}
    />
  );
}











function isQuickPatchActive(
  filters: FilterState,
  patch: Partial<FilterState>,
): boolean {
  return Object.entries(patch).every(
    ([key, value]) => (filters as RowMap)[key] === value,
  );
}

function buildClearedQuickPatch(
  patch: Partial<FilterState>,
): Partial<FilterState> {
  return Object.keys(patch).reduce<Partial<FilterState>>((result, key) => {
    const typedKey = key as keyof FilterState;
    const defaultValue = DEFAULT_FILTERS[typedKey];
    (result as RowMap)[key] =
      defaultValue !== undefined
        ? defaultValue
        : typeof (patch as RowMap)[key] === "boolean"
          ? false
          : null;
    return result;
  }, {});
}




type ScreenerFilterWorkbenchProps = {
  workspaceId: string;
  filters: FilterState;
  advancedSection: AdvancedFilterSection;
  onSectionChange: (section: AdvancedFilterSection) => void;
  sectorOptions: Array<{ code: string; label: string; helper?: string }>;
  onUpdateFilter: (patch: Partial<FilterState>) => void;
  onToggleStyleTag: (tag: string) => void;
};

function FilterPresetChip({
  label,
  selected = false,
  onClick,
}: {
  label: string;
  selected?: boolean;
  onClick: () => void;
}) {
  return (
    <Chip
      clickable
      size="small"
      label={label}
      onClick={onClick}
      sx={{
        height: 26,
        borderRadius: 1,
        bgcolor: selected ? "#111827" : "#fff",
        color: selected ? "#fff" : UI.sub,
        border: `1px solid ${selected ? "#111827" : UI.line}`,
        fontSize: 10.5,
        fontWeight: 850,
        "&:hover": { bgcolor: selected ? "#111827" : "#f8fafc" },
      }}
    />
  );
}


function FilterMetricCard({
  title,
  description,
  active = false,
  children,
}: {
  title: React.ReactNode;
  description: string;
  active?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        p: { xs: 1.6, lg: 1.9 },
        minWidth: 0,
        minHeight: 150,
        bgcolor: active ? "#f7f8ff" : "#fff",
        border: `1px solid ${active ? "#bfc7f6" : UI.lineSoft}`,
        borderRadius: 2.5,
        boxShadow: active ? "0 4px 14px rgba(54,70,173,.06)" : "none",
        transition: "border-color .18s,background .18s",
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={0.9}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: UI.text, fontSize: 13, fontWeight: 850, lineHeight: 1.4 }}>
            {title}
          </Typography>
          <Typography sx={{ mt: 0.45, color: UI.muted, fontSize: 11, lineHeight: 1.55 }}>
            {description}
          </Typography>
        </Box>
        {active ? (
          <Box component="span" sx={{ flexShrink: 0, px: 0.8, py: 0.28, borderRadius: 1.3,
            bgcolor: "#e9ecff", color: UI.blue, fontSize: 10, fontWeight: 800 }}>
            적용
          </Box>
        ) : null}
      </Stack>
      <Box sx={{ mt: 1.3, minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

function FilterRangeFields({
  minLabel = "이상",
  maxLabel = "이하",
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
  mode = "decimal",
  minPlaceholder,
  maxPlaceholder,
}: {
  minLabel?: string;
  maxLabel?: string;
  minValue: number | null | undefined;
  maxValue: number | null | undefined;
  onMinChange: (value: number | null) => void;
  onMaxChange: (value: number | null) => void;
  mode?: NumberFieldMode;
  minPlaceholder?: string;
  maxPlaceholder?: string;
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        gap: 0.65,
        minWidth: 0,
      }}
    >
      <NumberField
        label={minLabel}
        value={minValue}
        onChange={onMinChange}
        mode={mode}
        placeholder={minPlaceholder}
      />
      <NumberField
        label={maxLabel}
        value={maxValue}
        onChange={onMaxChange}
        mode={mode}
        placeholder={maxPlaceholder}
      />
    </Box>
  );
}

function ScreenerFilterWorkbench({
  workspaceId,
  filters,
  advancedSection,
  onSectionChange,
  sectorOptions,
  onUpdateFilter,
  onToggleStyleTag,
}: ScreenerFilterWorkbenchProps) {
  return (
    <Box id={workspaceId}>
      <Box
        className="pi-screener-viewbar"
        sx={{
          display: "flex",
          gap: 0.8,
          px: { xs: 1.2, md: 2.2 },
          py: 1.15,
          overflowX: "auto",
          borderBottom: `1px solid ${UI.lineSoft}`,
          bgcolor: "#fff",
          scrollbarWidth: "none",
        }}
      >
        {ADVANCED_FILTER_SECTIONS.map((section) => {
          const selected = advancedSection === section.key;
          return (
            <Button
              key={section.key}
              onClick={() => onSectionChange(section.key)}
              title={section.helper}
              sx={{
                minWidth: "auto",
                px: 1.7,
                py: 0.9,
                borderRadius: 2,
                border: `1px solid ${selected ? "#c3caf6" : UI.lineSoft}`,
                color: selected ? UI.blue : UI.sub,
                bgcolor: selected ? "#f0f2ff" : "#fff",
                fontSize: 12.5,
                fontWeight: selected ? 850 : 680,
                whiteSpace: "nowrap",
                "&:hover": { bgcolor: selected ? "#eef3ff" : "#f4f6f8" },
              }}
            >
              {section.label}
            </Button>
          );
        })}
      </Box>

      <Box sx={{ p: { xs: 1.4, md: 2.2 }, bgcolor: UI.soft }}>
        {advancedSection === "BASIC" ? (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))", xl: "repeat(4,minmax(0,1fr))" }, gap: 1.15, border: "none", borderRadius: 0, overflow: "visible", bgcolor: "transparent" }}>
            <FilterMetricCard title="업종" description="원하는 업종만 빠르게 좁힙니다." active={fieldActive(filters, "sectorCode")}>
              <FormControl size="small" fullWidth>
                <InputLabel>업종 선택</InputLabel>
                <Select
                  label="업종 선택"
                  value={filters.sectorCode || ""}
                  onChange={(event) => onUpdateFilter({ sectorCode: event.target.value || null })}
                  sx={{ height: 40, bgcolor: "#fff" }}
                >
                  <MenuItem value="">전체 업종</MenuItem>
                  {sectorOptions.map((item) => <MenuItem key={item.code} value={item.code}>{item.label}</MenuItem>)}
                </Select>
              </FormControl>
            </FilterMetricCard>

            <FilterMetricCard title="시가총액" description="기업 규모의 최소값입니다." active={fieldActive(filters, "minMarketCap")}>
              <Stack spacing={0.55}>
                <Stack direction="row" spacing={0.4} flexWrap="wrap" useFlexGap>
                  <FilterPresetChip label="3,000억+" selected={filters.minMarketCap === 300_000_000_000} onClick={() => onUpdateFilter({ minMarketCap: 300_000_000_000 })} />
                  <FilterPresetChip label="1조+" selected={filters.minMarketCap === 1_000_000_000_000} onClick={() => onUpdateFilter({ minMarketCap: 1_000_000_000_000 })} />
                </Stack>
                <NumberField label="최소 시총(원)" value={filters.minMarketCap} onChange={(v) => onUpdateFilter({ minMarketCap: v })} mode="money" placeholder="예: 300,000,000,000" />
              </Stack>
            </FilterMetricCard>

            <FilterMetricCard title="거래대금" description="유동성이 너무 낮은 종목을 제외합니다." active={fieldActive(filters, "minTradingValue")}>
              <Stack spacing={0.55}>
                <Stack direction="row" spacing={0.4} flexWrap="wrap" useFlexGap>
                  <FilterPresetChip label="10억+" selected={filters.minTradingValue === 1_000_000_000} onClick={() => onUpdateFilter({ minTradingValue: 1_000_000_000 })} />
                  <FilterPresetChip label="50억+" selected={filters.minTradingValue === 5_000_000_000} onClick={() => onUpdateFilter({ minTradingValue: 5_000_000_000 })} />
                </Stack>
                <NumberField label="최소 거래대금(원)" value={filters.minTradingValue} onChange={(v) => onUpdateFilter({ minTradingValue: v })} mode="money" placeholder="예: 1,000,000,000" />
              </Stack>
            </FilterMetricCard>

            <FilterMetricCard title="등락률" description="오늘 가격 등락 범위를 제한합니다." active={fieldActive(filters, "minPriceChangePct") || fieldActive(filters, "maxPriceChangePct")}>
              <FilterRangeFields minLabel="이상(%)" maxLabel="이하(%)" minValue={filters.minPriceChangePct} maxValue={filters.maxPriceChangePct} onMinChange={(v) => onUpdateFilter({ minPriceChangePct: v })} onMaxChange={(v) => onUpdateFilter({ maxPriceChangePct: v })} minPlaceholder="예: -3" maxPlaceholder="예: 5" />
            </FilterMetricCard>
          </Box>
        ) : null}

        {advancedSection === "FUNDAMENTAL" ? (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))", xl: "repeat(4,minmax(0,1fr))" }, gap: 1.15, border: "none", borderRadius: 0, overflow: "visible", bgcolor: "transparent" }}>
            <FilterMetricCard title={<HelpLabel label="PER" help={METRIC_HELP.per} />} description="이익 대비 가격 범위" active={fieldActive(filters, "minPer") || fieldActive(filters, "maxPer")}>
              <FilterRangeFields minValue={filters.minPer} maxValue={filters.maxPer} onMinChange={(v) => onUpdateFilter({ minPer: v })} onMaxChange={(v) => onUpdateFilter({ maxPer: v })} minPlaceholder="예: 0" maxPlaceholder="예: 15" />
            </FilterMetricCard>
            <FilterMetricCard title={<HelpLabel label="PBR" help={METRIC_HELP.pbr} />} description="자산 대비 가격 범위" active={fieldActive(filters, "minPbr") || fieldActive(filters, "maxPbr")}>
              <FilterRangeFields minValue={filters.minPbr} maxValue={filters.maxPbr} onMinChange={(v) => onUpdateFilter({ minPbr: v })} onMaxChange={(v) => onUpdateFilter({ maxPbr: v })} minPlaceholder="예: 0" maxPlaceholder="예: 1.5" />
            </FilterMetricCard>
            <FilterMetricCard title={<HelpLabel label="ROE" help={METRIC_HELP.roe} />} description="자기자본 수익성의 최소값" active={fieldActive(filters, "minRoe")}>
              <NumberField label="최소 ROE(%)" value={filters.minRoe} onChange={(v) => onUpdateFilter({ minRoe: v })} placeholder="예: 10" />
            </FilterMetricCard>
            <FilterMetricCard title="ROIC-WACC" description="자본비용을 넘는 수익성의 최소값" active={fieldActive(filters, "minRoicWaccSpread")}>
              <NumberField label="최소 스프레드(%p)" value={filters.minRoicWaccSpread} onChange={(v) => onUpdateFilter({ minRoicWaccSpread: v })} placeholder="예: 3" />
            </FilterMetricCard>
            <FilterMetricCard title="매출 3Y CAGR" description="최근 3년 매출 성장률의 최소값" active={fieldActive(filters, "minRevenueCagr3y")}>
              <NumberField label="최소 성장률(%)" value={filters.minRevenueCagr3y} onChange={(v) => onUpdateFilter({ minRevenueCagr3y: v })} placeholder="예: 5" />
            </FilterMetricCard>
            <FilterMetricCard title="영업이익률" description="영업 수익성의 최소값" active={fieldActive(filters, "minOperatingMargin")}>
              <NumberField label="최소 영업이익률(%)" value={filters.minOperatingMargin} onChange={(v) => onUpdateFilter({ minOperatingMargin: v })} placeholder="예: 8" />
            </FilterMetricCard>
            <FilterMetricCard title={<HelpLabel label="FCF Yield" help={METRIC_HELP.fcfYield} />} description="현금흐름 수익률의 최소값" active={fieldActive(filters, "minFcfYield")}>
              <NumberField label="최소 FCF Yield(%)" value={filters.minFcfYield} onChange={(v) => onUpdateFilter({ minFcfYield: v })} placeholder="예: 3" />
            </FilterMetricCard>
            <FilterMetricCard title="부채비율" description="부채 부담의 최대값" active={fieldActive(filters, "maxDebtToEquity")}>
              <NumberField label="최대 부채비율(%)" value={filters.maxDebtToEquity} onChange={(v) => onUpdateFilter({ maxDebtToEquity: v })} placeholder="예: 150" />
            </FilterMetricCard>
          </Box>
        ) : null}

        {advancedSection === "FLOW_TECH" ? (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))", xl: "repeat(4,minmax(0,1fr))" }, gap: 1.15, border: "none", borderRadius: 0, overflow: "visible", bgcolor: "transparent" }}>
            <FilterMetricCard title="20일 수급" description="외국인·기관의 순매수 여부" active={!!filters.foreignNetBuy20dPositive || !!filters.institutionNetBuy20dPositive}>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                <SelectableChip selected={!!filters.foreignNetBuy20dPositive} label="외국인 순매수" onClick={() => onUpdateFilter({ foreignNetBuy20dPositive: !filters.foreignNetBuy20dPositive })} />
                <SelectableChip selected={!!filters.institutionNetBuy20dPositive} label="기관 순매수" onClick={() => onUpdateFilter({ institutionNetBuy20dPositive: !filters.institutionNetBuy20dPositive })} />
              </Stack>
            </FilterMetricCard>
            <FilterMetricCard title="5일 수급" description="최근 단기 수급 확인" active={!!filters.foreignNetBuy5dPositive || !!filters.institutionNetBuy5dPositive}>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                <SelectableChip selected={!!filters.foreignNetBuy5dPositive} label="외국인 순매수" onClick={() => onUpdateFilter({ foreignNetBuy5dPositive: !filters.foreignNetBuy5dPositive })} />
                <SelectableChip selected={!!filters.institutionNetBuy5dPositive} label="기관 순매수" onClick={() => onUpdateFilter({ institutionNetBuy5dPositive: !filters.institutionNetBuy5dPositive })} />
              </Stack>
            </FilterMetricCard>
            <FilterMetricCard title={<HelpLabel label="거래대금 배율" help={METRIC_HELP.tradingValueRatio} />} description="20일 평균 대비 거래 활력" active={fieldActive(filters, "minTradingValueRatio20d")}>
              <NumberField label="최소 배율" value={filters.minTradingValueRatio20d} onChange={(v) => onUpdateFilter({ minTradingValueRatio20d: v })} placeholder="예: 1.5" />
            </FilterMetricCard>
            <FilterMetricCard title={<HelpLabel label="20일 변동성" help={METRIC_HELP.hv20} />} description="가격 흔들림 범위" active={fieldActive(filters, "minHistoricalVolatility20d") || fieldActive(filters, "maxHistoricalVolatility20d")}>
              <FilterRangeFields minLabel="이상(%)" maxLabel="이하(%)" minValue={filters.minHistoricalVolatility20d} maxValue={filters.maxHistoricalVolatility20d} onMinChange={(v) => onUpdateFilter({ minHistoricalVolatility20d: v })} onMaxChange={(v) => onUpdateFilter({ maxHistoricalVolatility20d: v })} minPlaceholder="예: 0" maxPlaceholder="예: 60" />
            </FilterMetricCard>
            <FilterMetricCard title={<HelpLabel label="Beta 60일" help={METRIC_HELP.beta60} />} description="시장 민감도의 최대값" active={fieldActive(filters, "maxStockBeta60d")}>
              <NumberField label="최대 Beta" value={filters.maxStockBeta60d} onChange={(v) => onUpdateFilter({ maxStockBeta60d: v })} placeholder="예: 1.2" />
            </FilterMetricCard>
            <FilterMetricCard title={<HelpLabel label="ETF 흐름점수" help={METRIC_HELP.etfFlowScore} />} description="섹터 자금 흐름의 최소값" active={fieldActive(filters, "minSectorFlowScore")}>
              <NumberField label="최소 점수" value={filters.minSectorFlowScore} onChange={(v) => onUpdateFilter({ minSectorFlowScore: v })} placeholder="예: 60" />
            </FilterMetricCard>
            <FilterMetricCard title={<HelpLabel label="매크로 정렬" help={METRIC_HELP.macroAlignment} />} description="현재 시장 환경과 종목 방향의 정렬" active={fieldActive(filters, "minMacroAlignmentScore")}>
              <NumberField label="최소 점수" value={filters.minMacroAlignmentScore} onChange={(v) => onUpdateFilter({ minMacroAlignmentScore: v })} placeholder="예: 55" />
            </FilterMetricCard>
            <FilterMetricCard title={<HelpLabel label="Macro Shock" help={METRIC_HELP.macroShock} />} description="시장 충격 부담의 최대값" active={fieldActive(filters, "maxMacroShockScore")}>
              <NumberField label="최대 점수" value={filters.maxMacroShockScore} onChange={(v) => onUpdateFilter({ maxMacroShockScore: v })} placeholder="예: 65" />
            </FilterMetricCard>
          </Box>
        ) : null}

        {advancedSection === "EVENT_RISK" ? (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))", xl: "repeat(4,minmax(0,1fr))" }, gap: 1.15, border: "none", borderRadius: 0, overflow: "visible", bgcolor: "transparent" }}>
            <FilterMetricCard title={<HelpLabel label="공시 영향" help={METRIC_HELP.disclosureImpact} />} description="최근 공시·이벤트 영향의 최소 점수" active={fieldActive(filters, "minDisclosureImpactScore")}>
              <NumberField label="최소 공시 영향" value={filters.minDisclosureImpactScore} onChange={(v) => onUpdateFilter({ minDisclosureImpactScore: v })} placeholder="예: 50" />
            </FilterMetricCard>
            <FilterMetricCard title={<HelpLabel label="52주 위치" help={METRIC_HELP.pricePosition52w} />} description="52주 저점=0, 고점=100 기준" active={fieldActive(filters, "minPricePosition52w") || fieldActive(filters, "maxPricePosition52w")}>
              <FilterRangeFields minLabel="이상(%)" maxLabel="이하(%)" minValue={filters.minPricePosition52w} maxValue={filters.maxPricePosition52w} onMinChange={(v) => onUpdateFilter({ minPricePosition52w: v })} onMaxChange={(v) => onUpdateFilter({ maxPricePosition52w: v })} minPlaceholder="예: 20" maxPlaceholder="예: 80" />
            </FilterMetricCard>
            <FilterMetricCard title="배당수익률" description="배당수익률의 최소 기준" active={fieldActive(filters, "minDividendYield")}>
              <NumberField label="최소 배당수익률(%)" value={filters.minDividendYield} onChange={(v) => onUpdateFilter({ minDividendYield: v })} placeholder="예: 3" />
            </FilterMetricCard>
            <FilterMetricCard title="위험 회피 제외" description="위험 회피 태그 종목을 결과에서 제거" active={!!filters.excludeRiskAvoid}>
              <SelectableChip selected={!!filters.excludeRiskAvoid} label={filters.excludeRiskAvoid ? "위험 종목 제외 중" : "위험 종목 제외"} onClick={() => onUpdateFilter({ excludeRiskAvoid: !filters.excludeRiskAvoid })} />
            </FilterMetricCard>
            <FilterMetricCard title="전략 태그" description="현재 보유한 전략 분류만 사용합니다." active={(filters.styleTags || []).length > 0}>
              <Stack direction="row" spacing={0.4} flexWrap="wrap" useFlexGap>
                {STYLE_OPTIONS.map((item) => (
                  <SelectableChip key={item.value} selected={(filters.styleTags || []).includes(item.value)} label={item.label} onClick={() => onToggleStyleTag(item.value)} />
                ))}
              </Stack>
            </FilterMetricCard>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}


function ScreenerResultTable({
  rows,
  viewMode,
  page,
  size,
  dense,
  loading,
  hasSearched,
  onOpen,
}: {
  rows: RowMap[];
  viewMode: ScreenerView;
  page: number;
  size: number;
  dense: boolean;
  loading: boolean;
  hasSearched: boolean;
  onOpen: (ticker: string, name: string) => void;
}) {
  const stickyHead = {
    bgcolor: "#f8f9fd",
    zIndex: 4,
  };

  return (
    <TableContainer
      sx={{
        display: { xs: "none", md: "block" },
        overflow: "auto",
        maxWidth: "100%",
        maxHeight: "min(70vh, 760px)",
        minHeight: 340,
        scrollbarColor: "#ccd4e5 #f8f9fd",
      }}
    >
      <Table
        className="pi-screener-table"
        size="small"
        stickyHeader
        sx={{
          minWidth: viewMode === "VALUE_FINANCIAL" ? 1350 : 1130,
          tableLayout: "auto",
          "& th": {
            color: UI.muted,
            fontSize: 11.3,
            fontWeight: 800,
            borderBottom: `1px solid ${UI.line}`,
            whiteSpace: "nowrap",
            py: dense ? 1.05 : 1.45,
            letterSpacing: "-.012em",
          },
          "& td": {
            borderBottom: `1px solid ${UI.lineSoft}`,
            py: dense ? 0.9 : 1.5,
            whiteSpace: "nowrap",
            fontSize: dense ? 11.4 : 12.2,
            fontVariantNumeric: "tabular-nums",
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell width={44} align="center" sx={{ ...stickyHead, position: "sticky", left: 0 }}>#</TableCell>
            <TableCell sx={{ ...stickyHead, minWidth: 188, position: "sticky", left: 44 }}>종목</TableCell>

            {viewMode === "GENERAL" ? <>
              <TableCell align="right">현재가</TableCell>
              <TableCell align="right">등락</TableCell>
              <TableCell align="right">시가총액</TableCell>
              <TableCell align="right">거래대금</TableCell>
              <TableCell align="right">공시 영향</TableCell>
              <TableCell align="right">종합</TableCell>
              <TableCell>판단</TableCell>
            </> : null}

            {viewMode === "VALUE_FINANCIAL" ? <>
              <TableCell align="right">중앙 추정가</TableCell>
              <TableCell align="right">대비</TableCell>
              <TableCell align="right">PER</TableCell>
              <TableCell align="right">PBR</TableCell>
              <TableCell align="right">매출 YoY</TableCell>
              <TableCell align="right">영업익 YoY</TableCell>
              <TableCell align="right">ROE</TableCell>
              <TableCell align="right">ROIC-WACC</TableCell>
              <TableCell align="right">FCF Yield</TableCell>
            </> : null}

            {viewMode === "PERFORMANCE" ? <>
              <TableCell align="right">샤프 60D</TableCell>
              <TableCell align="right">샤프 1Y</TableCell>
              <TableCell align="right">연환산 수익</TableCell>
              <TableCell align="right">변동성</TableCell>
              <TableCell align="right">최대낙폭</TableCell>
              <TableCell align="right">52주 위치</TableCell>
            </> : null}

            {viewMode === "FLOW" ? <>
              <TableCell align="right">외국인 5D</TableCell>
              <TableCell align="right">기관 5D</TableCell>
              <TableCell align="right">외국인 20D</TableCell>
              <TableCell align="right">기관 20D</TableCell>
              <TableCell align="right">거래배율</TableCell>
              <TableCell align="right">섹터 흐름</TableCell>
            </> : null}

            {viewMode === "TECHNICAL" ? <>
              <TableCell align="right">52주 위치</TableCell>
              <TableCell align="right">HV20</TableCell>
              <TableCell align="right">ATR14</TableCell>
              <TableCell align="right">Beta</TableCell>
              <TableCell align="right">거래배율</TableCell>
              <TableCell align="right">Macro Shock</TableCell>
            </> : null}

            {viewMode === "EVENT" ? <>
              <TableCell>업종</TableCell>
              <TableCell align="right">ETF 등락</TableCell>
              <TableCell align="right">매크로 정렬</TableCell>
              <TableCell align="right">공시 영향</TableCell>
              <TableCell>스타일</TableCell>
              <TableCell>신호</TableCell>
            </> : null}

            <TableCell width={72} align="center">상세</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map((row, idx) => {
            const ticker = String(get(row, "ticker") || idx);
            const name = String(get(row, "corpName") || get(row, "corp_name") || "-");
            const score = get(row, "professionalScreenerScore");
            const signal = deriveSignal(row);
            const fairValue = resolveScreenerFairValue(row);
            const rank = page * size + idx + 1;

            return (
              <TableRow
                key={ticker}
                hover
                onClick={() => onOpen(ticker, name)}
                sx={{
                  cursor: "pointer",
                  bgcolor: "#fff",
                  transition: "background .12s ease",
                  "&:hover": { bgcolor: "#f2f5ff" },
                  "&:focus-within": { bgcolor: "#f2f5ff" },
                }}
              >
                <TableCell align="center" sx={{ color: UI.muted, position: "sticky", left: 0, zIndex: 2, bgcolor: "inherit" }}>{rank}</TableCell>
                <TableCell sx={{ position: "sticky", left: 44, zIndex: 2, bgcolor: "inherit", boxShadow: "3px 0 5px -4px rgba(18,31,59,.28)" }}>
                  <Typography sx={{ color: UI.text, fontSize: 13.2, fontWeight: 850, lineHeight: 1.2 }}>{name}</Typography>
                  <Typography sx={{ mt: 0.25, color: UI.muted, fontSize: 10.5 }}>{ticker} · {get(row, "market") || "-"}</Typography>
                </TableCell>

                {viewMode === "GENERAL" ? <>
                  <TableCell align="right" sx={{ color: UI.text, fontWeight: 850 }}>{formatNumber(get(row, "closePrice"))}</TableCell>
                  <TableCell align="right" sx={{ color: toneColor(metricTone(get(row, "priceChangePct"))), fontWeight: 900 }}>{formatSignedPct(get(row, "priceChangePct"))}</TableCell>
                  <TableCell align="right">{formatWon(get(row, "marketCap"))}</TableCell>
                  <TableCell align="right">{formatWon(get(row, "tradingValue"))}</TableCell>
                  <TableCell align="right">{formatNumber(get(row, "disclosureImpactScore"), 1)}</TableCell>
                  <TableCell align="right" sx={{ color: scoreColor(score), fontWeight: 950 }}>{formatNumber(score, 1)}</TableCell>
                  <TableCell><Chip label={signal.label} size="small" sx={{ height: 20, bgcolor: alpha(toneColor(signal.tone), .08), color: toneColor(signal.tone), fontSize: 10, fontWeight: 850 }} /></TableCell>
                </> : null}

                {viewMode === "VALUE_FINANCIAL" ? <>
                  <TableCell align="right" sx={{ color: toneColor(fairValue.tone), fontWeight: 900 }}>{formatPriceWon(fairValue.fairPrice)}</TableCell>
                  <TableCell align="right" sx={{ color: toneColor(fairValue.tone), fontWeight: 900 }}>{formatSignedPct(fairValue.upsidePct, 1)}</TableCell>
                  <TableCell align="right">{formatNumber(get(row, "per"), 2)}</TableCell>
                  <TableCell align="right">{formatNumber(get(row, "pbr"), 2)}</TableCell>
                  <TableCell align="right" sx={{ color: toneColor(metricTone(get(row, "revenueGrowthYoyPct"))), fontWeight: 850 }}>{formatSignedPct(get(row, "revenueGrowthYoyPct"), 1)}</TableCell>
                  <TableCell align="right" sx={{ color: toneColor(metricTone(get(row, "operatingIncomeGrowthYoyPct"))), fontWeight: 850 }}>{formatSignedPct(get(row, "operatingIncomeGrowthYoyPct"), 1)}</TableCell>
                  <TableCell align="right">{formatPct(get(row, "roePct"), 1)}</TableCell>
                  <TableCell align="right" sx={{ color: toneColor(metricTone(get(row, "actualRoicWaccSpreadPctp"))), fontWeight: 850 }}>{formatSignedPct(get(row, "actualRoicWaccSpreadPctp"), 1)}</TableCell>
                  <TableCell align="right">{formatPct(get(row, "fcfYieldPct"), 1)}</TableCell>
                </> : null}

                {viewMode === "PERFORMANCE" ? <>
                  <TableCell align="right">{formatSharpe(get(row, "sharpeRatio60d"))}</TableCell>
                  <TableCell align="right">{formatSharpe(get(row, "sharpeRatio252d"))}</TableCell>
                  <TableCell align="right">{formatSignedPct(get(row, "annualizedReturn252dPct"))}</TableCell>
                  <TableCell align="right">{formatPct(get(row, "annualizedVolatility252dPct"))}</TableCell>
                  <TableCell align="right" sx={{ color: toneColor(drawdownTone(get(row, "maxDrawdown252dPct"))) }}>{formatPct(get(row, "maxDrawdown252dPct"))}</TableCell>
                  <TableCell align="right">{formatPricePosition52w(row)}</TableCell>
                </> : null}

                {viewMode === "FLOW" ? <>
                  <TableCell align="right">{formatSignedPct(get(row, "foreignNetBuy5dToTradingValuePct"))}</TableCell>
                  <TableCell align="right">{formatSignedPct(get(row, "institutionNetBuy5dToTradingValuePct"))}</TableCell>
                  <TableCell align="right">{formatSignedPct(get(row, "foreignNetBuy20dToTradingValuePct"))}</TableCell>
                  <TableCell align="right">{formatSignedPct(get(row, "institutionNetBuy20dToTradingValuePct"))}</TableCell>
                  <TableCell align="right">{formatMultiple(get(row, "tradingValueRatio20d"), 2)}</TableCell>
                  <TableCell align="right">{formatNumber(get(row, "sectorEtfFlowScore"), 1)}</TableCell>
                </> : null}

                {viewMode === "TECHNICAL" ? <>
                  <TableCell align="right">{formatPricePosition52w(row)}</TableCell>
                  <TableCell align="right">{formatPct(get(row, "historicalVolatility20dPct"), 1)}</TableCell>
                  <TableCell align="right">{formatAtr14dCompact(row)}</TableCell>
                  <TableCell align="right">{formatNumber(get(row, "stockBetaEffective") ?? get(row, "stockBeta60d"), 2)}</TableCell>
                  <TableCell align="right">{formatMultiple(get(row, "tradingValueRatio20d"), 2)}</TableCell>
                  <TableCell align="right">{formatNumber(get(row, "macroShockScore"), 1)}</TableCell>
                </> : null}

                {viewMode === "EVENT" ? <>
                  <TableCell>{get(row, "sectorName") || get(row, "sectorCode") || "-"}</TableCell>
                  <TableCell align="right">{formatSignedPct(get(row, "sectorPctChange1d"))}</TableCell>
                  <TableCell align="right">{formatNumber(get(row, "macroAlignmentScore"), 1)}</TableCell>
                  <TableCell align="right">{formatNumber(get(row, "disclosureImpactScore"), 1)}</TableCell>
                  <TableCell>{styleLabel(get(row, "screenerStyleTag"))}</TableCell>
                  <TableCell>{signal.label}</TableCell>
                </> : null}

                <TableCell align="center">
                  <IconButton
                    size="small"
                    aria-label={`${name} 종목 상세 열기`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpen(ticker, name);
                    }}
                    sx={{ color: UI.navy }}
                  >
                    <OpenInNewRoundedIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}

          {!loading && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={screenerTableColumnCount(viewMode)} align="center" sx={{ py: 8, color: UI.muted }}>
                {hasSearched ? "조건에 맞는 종목이 없습니다." : "조건을 설정한 뒤 스크리닝을 실행해 주세요."}
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </TableContainer>
  );
}


type ScreenerMobileResultsProps = {
  rows: RowMap[];
  viewMode: ScreenerView;
  page: number;
  size: number;
  onOpen: (ticker: string, name: string) => void;
};

function ScreenerMobileResults({
  rows,
  viewMode,
  page,
  size,
  onOpen,
}: ScreenerMobileResultsProps) {
  return (
    <Box sx={{ display: { xs: "block", md: "none" }, p: 1.4 }}>
      <Stack spacing={1}>
        {rows.map((row, idx) => {
          const ticker = String(get(row, "ticker") || idx);
          const name = String(get(row, "corpName") || get(row, "corp_name") || "-");
          const priceChange = get(row, "priceChangePct");
          const signal = deriveSignal(row);
          const fairValue = resolveScreenerFairValue(row);
          const rank = page * size + idx + 1;

          const metrics =
            viewMode === "VALUE_FINANCIAL"
              ? [
                  ["PER", formatNumber(get(row, "per"), 2)],
                  ["PBR", formatNumber(get(row, "pbr"), 2)],
                  ["ROIC-WACC", formatSignedPct(get(row, "actualRoicWaccSpreadPctp"), 1)],
                  ["FCF Yield", formatPct(get(row, "fcfYieldPct"), 1)],
                ]
              : viewMode === "PERFORMANCE"
                ? [
                    ["샤프 1Y", formatSharpe(get(row, "sharpeRatio252d"))],
                    ["연환산 수익", formatSignedPct(get(row, "annualizedReturn252dPct"))],
                    ["변동성", formatPct(get(row, "annualizedVolatility252dPct"))],
                    ["최대낙폭", formatPct(get(row, "maxDrawdown252dPct"))],
                  ]
                : viewMode === "FLOW"
                  ? [
                      ["외국인 5D", formatSignedPct(get(row, "foreignNetBuy5dToTradingValuePct"))],
                      ["기관 5D", formatSignedPct(get(row, "institutionNetBuy5dToTradingValuePct"))],
                      ["거래배율", `${formatMultiple(get(row, "tradingValueRatio20d"), 2)}`],
                      ["섹터 흐름", formatNumber(get(row, "sectorEtfFlowScore"), 1)],
                    ]
                  : viewMode === "TECHNICAL"
                    ? [
                        ["52주", formatPricePosition52w(row)],
                        ["HV20", formatPct(get(row, "historicalVolatility20dPct"), 1)],
                        ["Beta", formatNumber(get(row, "stockBetaEffective") ?? get(row, "stockBeta60d"), 2)],
                        ["거래배율", `${formatMultiple(get(row, "tradingValueRatio20d"), 2)}`],
                      ]
                    : viewMode === "EVENT"
                      ? [
                          ["ETF", formatSignedPct(get(row, "sectorPctChange1d"))],
                          ["매크로", formatNumber(get(row, "macroAlignmentScore"), 1)],
                          ["공시 영향", formatNumber(get(row, "disclosureImpactScore"), 1)],
                          ["스타일", styleLabel(get(row, "screenerStyleTag"))],
                        ]
                      : [
                          ["시가총액", formatWon(get(row, "marketCap"))],
                          ["거래대금", formatWon(get(row, "tradingValue"))],
                          ["공시 영향", formatNumber(get(row, "disclosureImpactScore"), 1)],
                          ["판단", signal.label],
                        ];

          return (
            <Paper
              key={`m-${ticker}`}
              elevation={0}
              role="button"
              tabIndex={0}
              onClick={() => onOpen(ticker, name)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onOpen(ticker, name);
                }
              }}
              sx={{
                p: 1.8,
                borderRadius: 2.7,
                border: `1px solid ${UI.line}`,
                bgcolor: "#fff",
                boxShadow: "0 5px 16px rgba(15,29,57,.035)",
                cursor: "pointer",
                outline: "none",
                "&:active": { bgcolor: "#f8fafc" },
              }}
            >
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: UI.text, fontSize: 15, fontWeight: 850 }} noWrap>{name}</Typography>
                  <Typography sx={{ color: UI.muted, fontSize: 10.8 }}>#{rank} · {ticker} · {get(row, "market") || "-"}</Typography>
                </Box>
                <Box sx={{ textAlign: "right", flex: "0 0 auto" }}>
                  <Typography sx={{ color: UI.text, fontSize: 13.5, fontWeight: 900 }}>{formatNumber(get(row, "closePrice"))}</Typography>
                  <Typography sx={{ color: toneColor(metricTone(priceChange)), fontSize: 11.8, fontWeight: 900 }}>{formatSignedPct(priceChange)}</Typography>
                </Box>
              </Stack>

              {viewMode === "VALUE_FINANCIAL" ? (
                <Stack direction="row" spacing={0.55} sx={{ mt: 0.55 }}>
                  <Chip label={`중앙 ${formatPriceWon(fairValue.fairPrice)}`} size="small" sx={{ height: 21, fontSize: 9.8, bgcolor: alpha(toneColor(fairValue.tone), .07), color: toneColor(fairValue.tone), fontWeight: 850 }} />
                  <Chip label={fairValue.label} size="small" sx={{ height: 21, fontSize: 9.8, bgcolor: "#f4f6f8", color: UI.sub, fontWeight: 850 }} />
                </Stack>
              ) : null}

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 1, mt: 1.4 }}>
                {metrics.map(([label, value]) => (
                  <MiniMetric key={label} label={label} value={value} />
                ))}
              </Box>
            </Paper>
          );
        })}

        {!rows.length ? (
          <Box sx={{ py: 5, textAlign: "center", color: UI.muted, fontSize: 12 }}>
            표시할 종목이 없습니다.
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
}


export default function ScreenerPage({ onGoStocks }: ScreenerPageProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [rows, setRows] = useState<RowMap[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [presetKey, setPresetKey] = useState<PresetKey>("ALL");
  const [sectorOptions, setSectorOptions] = useState<Array<{ code: string; label: string; helper?: string }>>([]);
  const [viewMode, setViewMode] = useState<ScreenerView>("GENERAL");
  const [resultKeyword, setResultKeyword] = useState("");
  const [advancedSection, setAdvancedSection] = useState<AdvancedFilterSection>("BASIC");
  const [dense, setDense] = useState(false);
  const [lastAppliedQuery, setLastAppliedQuery] = useState<string | null>(null);
  const latestSearchSeq = useRef(0);

  const page = filters.page || 0;
  const size = filters.size || 50;
  const request = useMemo( () => ({ ...filters, page, size }), [filters, page, size] );

  const initialLandingKeyword = useMemo(() => {
    const tickerParam = normalizeTickerParam(searchParams.get("ticker"));
    if (tickerParam) return tickerParam;
    return String(
      searchParams.get("keyword") ||
        searchParams.get("q") ||
        searchParams.get("stock") ||
        "",
    ).trim();
  }, [searchParams]);

  const chips = useMemo(() => activeFilterChips(filters), [filters]);
  const screeningExpression = useMemo(() => {
    const parts = chips.map((chip) => chip.label).filter(Boolean);
    return parts.length ? parts.join(" · ") : "전체 시장 · 기본 위험회피 제외";
  }, [chips]);

  const updateFilter = useCallback((patch: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...patch, page: 0 }));
  }, []);

  const analyticsMeta = useCallback(
    (targetFilters: FilterState = filters) => {
      const keyword = String(targetFilters.keyword || "").trim();
      const styleTags = (targetFilters.styleTags || []).join(",");
      return {
        source: "screener",
        screen_name: "professional_screener",
        preset: presetKey,
        market: String(targetFilters.market || "ALL"),
        sort_by: String(targetFilters.sortBy || "score"),
        sort_direction: String(targetFilters.sortDirection || "DESC"),
        active_filter_count: activeFilterChips(targetFilters).length,
        page: Number(targetFilters.page || 0),
        page_size: Number(targetFilters.size || 50),
        keyword: keyword || undefined,
        style_tags: styleTags || undefined,
      };
    },
    [filters, presetKey],
  );

  const load = useCallback(
    async (
      nextRequest: ScreenerSearchRequest = request,
      trigger = "manual",
    ) => {
      const startedAt = Date.now();
      const requestSeq = ++latestSearchSeq.current;
      setHasSearched(true);
      setLoading(true);
      setError(null);
      try {
        const data: any = await searchProfessionalScreener(nextRequest);
        if (requestSeq !== latestSearchSeq.current) return;
        const nextRows = Array.isArray(data?.items) ? data.items : [];
        const nextHasNext = Boolean(data?.hasNext);
        const nextPage = nextRequest.page || 0;
        const nextSize = nextRequest.size || 50;
        const rawTotalCount = Number(data?.totalCount);
        const nextTotalCount = data?.totalCount !== undefined && data?.totalCount !== null
          && Number.isFinite(rawTotalCount) && rawTotalCount >= 0
          ? rawTotalCount
          : nextHasNext ? (nextPage + 2) * nextSize : nextPage * nextSize + nextRows.length;

        setRows(nextRows);
        setLastAppliedQuery(JSON.stringify(nextRequest));
        setHasNext(nextHasNext);

        setTotalCount(nextTotalCount);

        trackUxEvent("screener_results_loaded", {
          ...analyticsMeta(nextRequest as FilterState),
          trigger,
          result_count: nextRows.length,
          total_count: Number(nextTotalCount || 0),
          elapsedMs: Date.now() - startedAt,
        });
      } catch (e: any) {
        if (requestSeq !== latestSearchSeq.current) return;
        const message =
          e?.response?.data?.message ||
          e?.message ||
          "스크리너 조회 중 오류가 발생했습니다.";
        setError(message);
        trackUxEvent("screener_results_error", {
          ...analyticsMeta(nextRequest as FilterState),
          trigger,
          message: String(message).slice(0, 160),
          elapsedMs: Date.now() - startedAt,
        });
      } finally {
        if (requestSeq === latestSearchSeq.current) setLoading(false);
      }
    },
    [analyticsMeta, request],
  );

  useEffect(() => {
    void getProfessionalScreenerFilterOptions()
      .then((data) => setSectorOptions(normalizeSectorOptions(data)))
      .catch(() => setSectorOptions([]));
  }, []);

  useEffect(() => {
    if (!initialLandingKeyword) return;
    const next = buildTickerLandingFilters(initialLandingKeyword, size);
    setPresetKey("ALL");
    setFilters(next);
    trackUxEvent("screener_landing_search", {
      source: "screener",
      screen_name: "professional_screener",
      keyword: initialLandingKeyword,
    });
    void load(next, "landing-keyword");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLandingKeyword]);

  const handleGoStockIntelligence = useCallback(
    (
      ticker?: string,
      stockName?: string,
      entryPoint = "result-detail",
    ) => {
      const normalizedTicker = String(ticker || "").trim();
      if (!normalizedTicker) return;

      trackUxEvent("screener_stock_open", {
        source: entryPoint,
        screen_name: "professional_screener",
        ticker: normalizedTicker,
        stock_code: normalizedTicker,
        stock_name: stockName || undefined,
        view_mode: viewMode,
      });

      // App.tsx still injects the old onGoStocks callback into /screener.
      // Do not call it for valid tickers: it opens the legacy /stocks UI.
      const normalized = normalizedTicker.match(/^A?(\d{6})$/)?.[1];
      if (normalized) {
        navigate(`/stock/${normalized}`, {
          state: { source: 'professional-screener', viewMode },
        });
        return;
      }
      // Keep legacy callback only for non-stock special entries.
      onGoStocks?.('Stock Intelligence', normalizedTicker);

    },
    [navigate, onGoStocks, viewMode],
  );

  const handleSelectStockSuggestion = useCallback(
    (stock: StockSearchSuggestion) => {
      const ticker = String(stock?.ticker || "").trim();
      if (!/^\d{6}$/.test(ticker)) return;
      const next: FilterState = {
        ...filters,
        keyword: ticker,
        minTradingValue: null,
        minScore: null,
        excludeRiskAvoid: false,
        page: 0,
      };

      trackUxEvent("screener_stock_search_select", {
        source: "screener-search",
        screen_name: "professional_screener",
        ticker,
        stock_code: ticker,
        stock_name: String((stock as any)?.name || (stock as any)?.corpName || "") || undefined,
      });

      setPresetKey("ALL");
      setFilters(next);

      const params = new URLSearchParams(searchParams);
      params.set("ticker", ticker);
      params.set("keyword", ticker);
      navigate(`/screener?${params.toString()}`, { replace: false });

      void load(next, "stock-suggestion");
    },
    [filters, load, navigate, searchParams],
  );

  const applyPreset = useCallback(
    (key: PresetKey) => {
      const preset = PRESETS.find((item) => item.key === key);
      if (!preset) return;
      const next = {
        ...DEFAULT_FILTERS,
        ...preset.patch,
        page: 0,
        size: filters.size || DEFAULT_FILTERS.size,
      };

      trackUxEvent("screener_preset_apply", {
        source: "preset",
        screen_name: "professional_screener",
        preset: key,
        preset_label: preset.label,
      });
      trackUxEvent("screener_filter_apply", {
        source: "preset",
        screen_name: "professional_screener",
        preset: key,
        active_filter_count: activeFilterChips(next).length,
        market: String(next.market || "ALL"),
        sort_by: String(next.sortBy || "score"),
      });

      setPresetKey(key);
      setFilters(next);
      setAdvancedOpen(false);
      void load(next, `preset:${key}`);
    },
    [filters.size, load],
  );

  const handleSearch = useCallback(() => {
    const next = { ...filters, page: 0 };
    trackUxEvent("screener_filter_apply", {
      ...analyticsMeta(next),
      source: "primary-search",
    });
    setAdvancedOpen(false);
    void load(next, "primary-search");
  }, [analyticsMeta, filters, load]);

  const handleReset = useCallback(() => {
    latestSearchSeq.current += 1;
    setLoading(false);
    trackUxEvent("screener_reset", {
      ...analyticsMeta(filters),
      source: "screener-toolbar",
    });
    setPresetKey("ALL");
    setAdvancedOpen(false);
    setAdvancedSection("BASIC");
    setError(null);
    setHasSearched(false);
    setFilters(DEFAULT_FILTERS);
    setRows([]);
    setTotalCount(0);
    setHasNext(false);
    setLastAppliedQuery(null);
  }, [analyticsMeta, filters]);

  const handlePrevPage = useCallback(() => {
    if (page <= 0 || loading) return;
    const next = { ...filters, page: page - 1 };
    trackUxEvent("screener_page_change", {
      source: "pagination",
      screen_name: "professional_screener",
      from_page: page + 1,
      to_page: page,
      direction: "prev",
    });
    setFilters(next);
    void load(next, "pagination-prev");
  }, [filters, load, loading, page]);

  const handleNextPage = useCallback(() => {
    if (!hasNext || loading) return;
    const next = { ...filters, page: page + 1 };
    trackUxEvent("screener_page_change", {
      source: "pagination",
      screen_name: "professional_screener",
      from_page: page + 1,
      to_page: page + 2,
      direction: "next",
    });
    setFilters(next);
    void load(next, "pagination-next");
  }, [filters, hasNext, load, loading, page]);

  const handleRowsPerPageChange = useCallback(
    (value: number) => {
      const next = { ...filters, page: 0, size: value };
      trackUxEvent("screener_page_size_change", {
        source: "pagination",
        screen_name: "professional_screener",
        page_size: value,
      });
      setFilters(next);
      void load(next, "page-size");
    },
    [filters, load],
  );

  const toggleStyleTag = useCallback(
    (tag: string) => {
      const current = filters.styleTags || [];
      const selected = !current.includes(tag);
      const next = selected
        ? [...current, tag]
        : current.filter((item) => item !== tag);

      trackUxEvent("screener_filter_change", {
        source: "advanced-filter",
        screen_name: "professional_screener",
        filter_key: "styleTags",
        filter_value: tag,
        selected,
      });

      updateFilter({ styleTags: next });
    },
    [filters.styleTags, updateFilter],
  );


  const applyQuickPatch = useCallback(
    (label: string, patch: Partial<FilterState>) => {
      const selected = !isQuickPatchActive(filters, patch);
      trackUxEvent("screener_quick_filter_toggle", {
        source: "quick-filter",
        screen_name: "professional_screener",
        label,
        selected,
      });

      setPresetKey("ALL");
      setFilters((prev) => {
        const nextPatch = isQuickPatchActive(prev, patch)
          ? buildClearedQuickPatch(patch)
          : patch;
        return { ...prev, ...nextPatch, page: 0 };
      });
    },
    [filters],
  );

  const clearActiveFilter = useCallback((key: string) => {
    trackUxEvent("screener_filter_remove", {
      source: "active-filter-chip",
      screen_name: "professional_screener",
      filter_key: key,
    });

    setPresetKey("ALL");
    setFilters((prev) => {
      if (key.startsWith("style-")) {
        const tag = key.slice("style-".length);
        return {
          ...prev,
          styleTags: (prev.styleTags || []).filter((item) => item !== tag),
          page: 0,
        };
      }

      const typedKey = key as keyof FilterState;
      const currentValue = prev[typedKey];

      // `excludeRiskAvoid` is enabled in DEFAULT_FILTERS for the initial screener
      // safety policy. An applied-filter chip delete action, however, means the
      // user explicitly wants this filter OFF. Resetting it to the default
      // (`true`) made the chip impossible to remove.
      const clearedValue =
        typedKey === "excludeRiskAvoid"
          ? false
          : DEFAULT_FILTERS[typedKey] !== undefined
            ? DEFAULT_FILTERS[typedKey]
            : typeof currentValue === "boolean"
              ? false
              : null;

      return { ...prev, [typedKey]: clearedValue, page: 0 };
    });
  }, []);

  const handleMarketChange = useCallback(
    (market: any) => {
      trackUxEvent("screener_filter_change", {
        source: "toolbar",
        screen_name: "professional_screener",
        filter_key: "market",
        filter_value: String(market),
      });
      updateFilter({ market });
    },
    [updateFilter],
  );

  const handleSortChange = useCallback(
    (sortBy: string) => {
      trackUxEvent("screener_sort_change", {
        source: "toolbar",
        screen_name: "professional_screener",
        sort_by: sortBy,
      });
      // Options explicitly labeled "낮은순" must sort ascending.
      updateFilter({ sortBy, sortDirection: ["per", "pbr", "pegRatio"].includes(sortBy) ? "ASC" : "DESC" });
    },
    [updateFilter],
  );

  const handleAdvancedToggle = useCallback(() => {
    const nextOpen = !advancedOpen;
    trackUxEvent("screener_filter_panel_toggle", {
      source: "toolbar",
      screen_name: "professional_screener",
      open: nextOpen,
      active_filter_count: chips.length,
    });
    setAdvancedOpen(nextOpen);
  }, [advancedOpen, chips.length]);

  const handleAdvancedSectionChange = useCallback(
    (section: AdvancedFilterSection) => {
      if (section === advancedSection) return;
      trackUxEvent("screener_filter_category_open", {
        source: "advanced-filter",
        screen_name: "professional_screener",
        category: section,
      });
      setAdvancedSection(section);
    },
    [advancedSection],
  );

  const handleViewModeChange = useCallback(
    (mode: ScreenerView) => {
      if (mode === viewMode) return;
      trackUxEvent("screener_view_change", {
        source: "result-column-set",
        screen_name: "professional_screener",
        from_view: viewMode,
        to_view: mode,
      });
      setViewMode(mode);
    },
    [viewMode],
  );

  const displayedRows = useMemo(() => {
    const query = resultKeyword.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) => {
      const ticker = String(get(row, "ticker") || "").toLowerCase();
      const name = String(get(row, "corpName") || get(row, "corp_name") || "").toLowerCase();
      const sector = String(get(row, "sectorName") || get(row, "sectorCode") || "").toLowerCase();
      return ticker.includes(query) || name.includes(query) || sector.includes(query);
    });
  }, [resultKeyword, rows]);

  const filtersDirty = hasSearched && lastAppliedQuery !== null && JSON.stringify(request) !== lastAppliedQuery;
  const currentPreset = PRESETS.find((item) => item.key === presetKey);
  const viewDescriptions: Record<ScreenerView, string> = {
    GENERAL: "가격 · 거래대금 · 점수 · 판단을 한눈에",
    VALUE_FINANCIAL: "가격평가 · 실적 성장 · 자본수익성 비교",
    FLOW: "외국인 · 기관 · 업종 자금 흐름 비교",
    PERFORMANCE: "샤프 · 변동성 · 하락 위험 비교",
    TECHNICAL: "52주 위치 · ATR · Beta · 거래량 변화",
    EVENT: "공시 · 업종 ETF · 매크로 연결 관측",
  };

  const viewTabs: Array<{ key: ScreenerView; label: string }> = [
    { key: "GENERAL", label: "개요" },
    { key: "VALUE_FINANCIAL", label: "가치·재무" },
    { key: "FLOW", label: "수급" },
    { key: "PERFORMANCE", label: "성과·위험" },
    { key: "TECHNICAL", label: "기술·변동성" },
    { key: "EVENT", label: "이슈·섹터" },
  ];

  return (
    <Container
      maxWidth={false}
      className="pi-screener-root"
      sx={{
        maxWidth: 1700,
        px: { xs: 1.5, sm: 2.5, lg: 4 },
        pt: { xs: 2.8, md: 4.2 },
        pb: { xs: 5, md: 8 },
        mx: "auto",
        bgcolor: "transparent",
        color: UI.text,
        "& .MuiButton-root": { textTransform: "none" },
      }}
    >
      <Stack spacing={{ xs: 2.2, md: 2.8 }}>
        {/* Editorial heading: existing global AppBar is the single brand header. */}
        <Box component="header" sx={{ display: "flex", gap: 2.5, justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap" }}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
              <Box sx={{ height: 7, width: 7, borderRadius: "50%", bgcolor: UI.blue }} />
              <Typography sx={{ fontSize: 11, letterSpacing: ".16em", color: UI.blue, fontWeight: 850 }}>
                EQUITY RESEARCH / SCREENER
              </Typography>
            </Stack>
            <Typography component="h1" sx={{ color: UI.text, fontSize: { xs: 27, sm: 35, xl: 39 }, fontWeight: 860, letterSpacing: "-.065em", lineHeight: 1.22 }}>
              종목 스크리너
            </Typography>
            <Typography sx={{ mt: 1.1, color: UI.sub, fontSize: { xs: 12.5, md: 14 }, lineHeight: 1.7, wordBreak: "keep-all" }}>
              종목의 현재 상태부터 확인할 수 있도록 조건을 좁히고, 재무·수급·위험의 근거를 비교합니다.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.6, py: 1.1, border: `1px solid ${UI.line}`, borderRadius: 2.3, bgcolor: "#fff", alignSelf: { xs: "flex-start", md: "flex-end" } }}>
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: hasSearched && !loading && !error ? UI.green : UI.muted }} />
            <Typography sx={{ color: UI.sub, fontSize: 11.5, fontWeight: 750 }}>
              {loading ? "조건 검색 중" : error ? "데이터 조회 오류" : hasSearched ? "조회 완료 · 데이터 기준일은 각 종목별 확인" : "분석 조건을 선택하세요"}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1.2, px: { xs: 1.4, md: 2 }, py: 1.25, bgcolor: "#edf1f8", border: "1px solid #e3e8f1", borderRadius: 2.4 }}>
          <Stack direction="row" spacing={1.1} alignItems="center" sx={{ flexWrap: "wrap" }}>
            <Typography sx={{ color: UI.muted, fontSize: 11, fontWeight: 720 }}>현재 탐색</Typography>
            <Typography sx={{ color: UI.text, fontSize: 12, fontWeight: 830 }}>{currentPreset?.label || "전체"}</Typography>
            <Box sx={{ width: 1, height: 12, bgcolor: "#cbd4e3" }} />
            <Typography sx={{ color: UI.sub, fontSize: 11.5 }}>조건 {chips.length}개</Typography>
            {filtersDirty ? <Typography sx={{ color: UI.amber, fontSize: 11, fontWeight: 850 }}>· 변경사항 미적용</Typography> : null}
          </Stack>
          <Typography sx={{ color: UI.sub, fontSize: 11.5 }}>
            {hasSearched ? `조회 일치 ${formatNumber(totalCount)}종목 · ${page + 1}페이지 ${rows.length}개` : "관점을 선택하거나 직접 조건을 입력하세요"}
          </Typography>
        </Box>

        <Paper elevation={0} component="section" aria-label="스크리닝 조건 설정" sx={{ border: `1px solid ${UI.line}`, borderRadius: { xs: 3, md: 4 }, bgcolor: "#fff", overflow: "hidden", boxShadow: "0 12px 36px rgba(25,40,71,.035)" }}>
          <Box sx={{ px: { xs: 1.8, md: 3.1 }, pt: { xs: 2, md: 2.7 }, pb: 1.8 }}>
            <Stack direction={{ xs: "column", md: "row" }} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between" spacing={1.4}>
              <Box>
                <Typography sx={{ color: UI.blue, fontSize: 10.5, letterSpacing: ".13em", fontWeight: 850 }}>01 / DISCOVERY</Typography>
                <Typography component="h2" sx={{ mt: 0.4, color: UI.text, fontWeight: 850, fontSize: { xs: 18, md: 21 }, letterSpacing: "-.035em" }}>어떤 종목을 찾고 있나요?</Typography>
                <Typography sx={{ mt: 0.35, color: UI.muted, fontSize: 11.5 }}>종목명 검색 또는 투자 전략 선택 후 조회할 수 있습니다.</Typography>
              </Box>
              <Stack direction="row" alignItems="center" spacing={0.8}>
                <Button size="small" startIcon={<TuneRoundedIcon />} onClick={handleAdvancedToggle} aria-expanded={advancedOpen} aria-controls="screener-filter-workspace"
                  sx={{ px: 1.7, py: 1, borderRadius: 2, fontSize: 12, fontWeight: 800, color: advancedOpen ? UI.blue : UI.text, bgcolor: advancedOpen ? "#edf0ff" : UI.soft, border: `1px solid ${advancedOpen ? "#c7cfff" : UI.lineSoft}`, "&:hover": { bgcolor: "#eaeefa" } }}>
                  {advancedOpen ? "상세 필터 닫기" : "상세 필터"}{chips.length ? ` · ${chips.length}` : ""}
                </Button>
                <Button size="small" startIcon={<RestartAltRoundedIcon />} onClick={handleReset} disabled={loading}
                  sx={{ px: 1.5, py: 1, borderRadius: 2, fontSize: 12, fontWeight: 760, color: UI.sub, "&:hover": { bgcolor: UI.soft } }}>초기화</Button>
              </Stack>
            </Stack>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "minmax(260px,1fr) 150px 200px 46px 120px" }, gap: 1, mt: 2.4, alignItems: "stretch" }}>
              <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" }, minWidth: 0 }}>
                <StockSearchSuggestInput
                  size="small"
                  label="종목 / 코드"
                  value={filters.keyword || ""}
                  onChange={(value) => updateFilter({ keyword: value })}
                  onEnter={handleSearch}
                  onSelect={handleSelectStockSuggestion}
                  selectedValueMode="ticker"
                  selectFirstOnEnter
                  placeholder="종목명 또는 6자리 코드 검색"
                  inputSx={{
                    "& .MuiOutlinedInput-root": { minHeight: 49, borderRadius: 2.2, bgcolor: "#fff", "& fieldset": { borderColor: UI.line }, "&:hover fieldset": { borderColor: "#a6b0c2" }, "&.Mui-focused fieldset": { borderColor: UI.blue, borderWidth: 1.5 } },
                    "& .MuiInputBase-input": { fontSize: 13, fontWeight: 750 },
                  }}
                />
              </Box>
              <FormControl size="small" sx={{ minWidth: 0 }}>
                <InputLabel>시장</InputLabel>
                <Select label="시장" value={filters.market || "ALL"} onChange={(event) => handleMarketChange(event.target.value as any)}
                  sx={{ height: 49, borderRadius: 2.2, bgcolor: "#fff", fontSize: 12.5 }}>
                  <MenuItem value="ALL">전체 시장</MenuItem><MenuItem value="KOSPI">KOSPI</MenuItem><MenuItem value="KOSDAQ">KOSDAQ</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 0 }}>
                <InputLabel>우선 정렬</InputLabel>
                <Select label="우선 정렬" value={filters.sortBy || "score"} onChange={(event) => handleSortChange(String(event.target.value))}
                  sx={{ height: 49, borderRadius: 2.2, bgcolor: "#fff", fontSize: 12.5 }}>
                  {SORT_OPTIONS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
                </Select>
              </FormControl>
              <Tooltip title={filters.sortDirection === "ASC" ? "오름차순 · 클릭하여 내림차순" : "내림차순 · 클릭하여 오름차순"} arrow>
                <Button aria-label="정렬 방향 전환" onClick={() => updateFilter({ sortDirection: filters.sortDirection === "ASC" ? "DESC" : "ASC" })}
                  sx={{ minWidth: 0, height: 49, borderRadius: 2.2, fontSize: 18, fontWeight: 900, border: `1px solid ${UI.line}`, color: UI.text, bgcolor: "#fff" }}>
                  {filters.sortDirection === "ASC" ? "↑" : "↓"}
                </Button>
              </Tooltip>
              <Button variant="contained" onClick={handleSearch} disabled={loading} startIcon={loading ? <CircularProgress size={17} color="inherit" /> : <SearchRoundedIcon />}
                sx={{ minHeight: 49, borderRadius: 2.2, bgcolor: UI.navy, fontSize: 13, fontWeight: 800, boxShadow: "none", "&:hover": { bgcolor: "#293752", boxShadow: "none" } }}>
                {loading ? "조회 중" : "검색"}
              </Button>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mt: 2.8, mb: 1.2, gap: 1.2, flexWrap: "wrap" }}>
              <Typography sx={{ color: UI.text, fontSize: 12.5, fontWeight: 850 }}>투자 관점으로 빠르게 찾기</Typography>
              <Typography sx={{ color: UI.muted, fontSize: 10.8 }}>프리셋 선택 시 해당 조건으로 즉시 조회합니다.</Typography>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", sm: "repeat(3,minmax(0,1fr))", lg: "repeat(6,minmax(0,1fr))" }, gap: 1 }}>
              {PRESETS.map((preset) => {
                const selected = presetKey === preset.key;
                return (
                  <Button key={preset.key} onClick={() => applyPreset(preset.key)} aria-pressed={selected}
                    sx={{ minWidth: 0, minHeight: 83, textAlign: "left", p: { xs: 1.3, lg: 1.55 }, display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", borderRadius: 2.4,
                      border: `1px solid ${selected ? "#b9c4fa" : UI.lineSoft}`, bgcolor: selected ? "#f1f3ff" : "#f9fafc", color: UI.text, boxShadow: selected ? "inset 0 0 0 1px #c8d1ff" : "none", "&:hover": { bgcolor: selected ? "#ecf0ff" : "#f1f4f9" } }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5} sx={{ width: "100%" }}>
                      <Typography sx={{ fontSize: 12.4, fontWeight: 850, color: selected ? UI.blue : UI.text }}>{preset.label}</Typography>
                      <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", border: `1.5px solid ${selected ? UI.blue : "#aeb7c9"}`, bgcolor: selected ? UI.blue : "transparent" }} />
                    </Stack>
                    <Typography sx={{ mt: 0.6, color: UI.muted, fontSize: 10.5, lineHeight: 1.4, textAlign: "left", wordBreak: "keep-all" }}>{preset.helper}</Typography>
                  </Button>
                );
              })}
            </Box>
          </Box>

          <Box sx={{ bgcolor: "#fafbfe", borderTop: `1px solid ${UI.lineSoft}`, px: { xs: 1.8, md: 3.1 }, py: 1.75 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flexWrap: "wrap" }}>
              <Typography sx={{ flexShrink: 0, color: UI.muted, fontSize: 11, fontWeight: 820 }}>빠른 조건</Typography>
              <Stack direction="row" useFlexGap spacing={0.7} sx={{ flexWrap: "wrap", flex: 1, minWidth: 0 }}>
                {[
                  { label: "시총 3,000억+", patch: { minMarketCap: 300_000_000_000 } },
                  { label: "거래대금 10억+", patch: { minTradingValue: 1_000_000_000 } },
                  { label: "PER 15 이하", patch: { minPer: 0, maxPer: 15 } },
                  { label: "ROE 10%+", patch: { minRoe: 10 } },
                  { label: "외국인 20일 순매수", patch: { foreignNetBuy20dPositive: true } },
                  { label: "기관 20일 순매수", patch: { institutionNetBuy20dPositive: true } },
                  { label: "공시 영향 50+", patch: { minDisclosureImpactScore: 50 } },
                ].map((item) => {
                  const selected = isQuickPatchActive(filters, item.patch);
                  return <Chip key={item.label} clickable size="small" label={item.label} onClick={() => applyQuickPatch(item.label, item.patch)}
                    sx={{ height: 31, borderRadius: 1.6, bgcolor: selected ? "#e9edff" : "#fff", color: selected ? UI.blue : UI.sub, border: `1px solid ${selected ? "#c1cafa" : UI.line}`, fontWeight: 750, fontSize: 11.2, "&:hover": { bgcolor: selected ? "#e2e8ff" : "#f1f4fa" } }} />;
                })}
              </Stack>
            </Box>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2, flexWrap: "wrap", mt: 1.5, pt: 1.4, borderTop: `1px solid ${UI.lineSoft}` }}>
              <Typography sx={{ flexShrink: 0, pt: 0.65, color: UI.muted, fontSize: 11, fontWeight: 820 }}>적용할 조건</Typography>
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.65} sx={{ flex: 1 }}>
                {chips.length ? chips.map((chip) => (
                  <Chip key={chip.key} size="small" label={chip.label} onDelete={() => clearActiveFilter(chip.key)}
                    sx={{ height: 27, borderRadius: 1.4, color: UI.sub, bgcolor: "#fff", border: `1px solid ${UI.lineSoft}`, fontSize: 10.6, fontWeight: 720, "& .MuiChip-deleteIcon": { color: UI.muted, fontSize: 16 } }} />
                )) : <Typography sx={{ color: UI.muted, fontSize: 11.5, py: 0.6 }}>추가 조건 없음</Typography>}
              </Stack>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "flex-start", mt: 1.4 }}>
              <Typography sx={{ color: UI.muted, fontSize: 10.5, lineHeight: 1.5, maxWidth: "90%", wordBreak: "keep-all" }}>검색식: {screeningExpression}</Typography>
              {filtersDirty ? <Typography sx={{ flexShrink: 0, color: UI.amber, fontSize: 11, fontWeight: 820 }}>다시 검색 필요</Typography> : null}
            </Box>
          </Box>

          <Collapse in={advancedOpen} timeout={180} unmountOnExit>
            <Box sx={{ borderTop: `1px solid ${UI.line}`, bgcolor: UI.soft }}>
              <Box sx={{ px: { xs: 1.6, md: 2.5 }, pt: 2.1, pb: 0.4 }}>
                <Typography sx={{ fontWeight: 850, fontSize: 14, color: UI.text }}>상세 조건 구성</Typography>
                <Typography sx={{ color: UI.muted, fontSize: 11.5, mt: 0.4 }}>재무·거래·수급·위험 항목을 조합합니다. 값이 없으면 조건에서 제외됩니다.</Typography>
              </Box>
              <ScreenerFilterWorkbench workspaceId="screener-filter-workspace" filters={filters} advancedSection={advancedSection} onSectionChange={handleAdvancedSectionChange} sectorOptions={sectorOptions} onUpdateFilter={updateFilter} onToggleStyleTag={toggleStyleTag} />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between" sx={{ px: { xs: 1.8, md: 3.1 }, py: 1.6, borderTop: `1px solid ${UI.lineSoft}` }}>
                <Typography sx={{ color: UI.muted, fontSize: 11.2 }}>데이터가 없는 지표는 임의의 0으로 채우지 않습니다.</Typography>
                <Button variant="contained" startIcon={<SearchRoundedIcon />} onClick={handleSearch} disabled={loading}
                  sx={{ minHeight: 43, px: 2.4, bgcolor: UI.navy, boxShadow: "none", borderRadius: 2.1, fontSize: 12.5, fontWeight: 800, "&:hover": { bgcolor: "#293752", boxShadow: "none" } }}>이 조건으로 결과 보기</Button>
              </Stack>
            </Box>
          </Collapse>
        </Paper>

        {error ? <Alert severity="error" sx={{ borderRadius: 2.2 }}>{error}</Alert> : null}

        <Paper elevation={0} component="section" aria-label="스크리닝 결과" sx={{ border: `1px solid ${UI.line}`, bgcolor: "#fff", borderRadius: { xs: 3, md: 4 }, overflow: "hidden", boxShadow: "0 12px 36px rgba(25,40,71,.035)" }}>
          <Box sx={{ px: { xs: 1.8, md: 3.1 }, pt: { xs: 2.1, md: 2.7 }, pb: 1.7 }}>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} alignItems={{ xs: "stretch", md: "center" }}>
              <Box>
                <Typography sx={{ color: UI.blue, fontSize: 10.5, letterSpacing: ".13em", fontWeight: 850 }}>02 / COMPARE</Typography>
                <Stack direction="row" spacing={1.3} alignItems="baseline" sx={{ mt: 0.3 }}>
                  <Typography component="h2" sx={{ fontSize: { xs: 19, md: 23 }, fontWeight: 850, letterSpacing: "-.04em", color: UI.text }}>탐색 결과</Typography>
                  <Typography sx={{ color: UI.blue, fontSize: 20, fontWeight: 850, fontVariantNumeric: "tabular-nums" }}>{hasSearched ? formatNumber(totalCount) : "—"}</Typography>
                  <Typography sx={{ color: UI.muted, fontSize: 11.5 }}>종목</Typography>
                </Stack>
                <Typography sx={{ mt: 0.6, fontSize: 11.5, color: UI.muted, lineHeight: 1.5 }}>
                  {filtersDirty ? "조건이 변경되었습니다. 현재 표는 이전 검색 결과이며 다시 검색하면 갱신됩니다." : "종목을 선택하면 현재 판단·변화·근거·재무·차트가 연결된 종목 화면으로 이동합니다."}
                </Typography>
              </Box>
              <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "center" }} spacing={1}>
                <TextField size="small" value={resultKeyword} onChange={(event) => setResultKeyword(event.target.value)} placeholder="현재 페이지 결과에서 찾기"
                  inputProps={{ "aria-label": "현재 페이지 결과에서 찾기" }}
                  sx={{ minWidth: { sm: 235 }, "& .MuiOutlinedInput-root": { height: 42, borderRadius: 2, bgcolor: UI.soft, fontSize: 12.2 } }} />
                <Button size="small" variant="outlined" onClick={() => setDense((v) => !v)} aria-pressed={dense}
                  sx={{ height: 42, px: 1.65, borderRadius: 2, borderColor: UI.line, color: UI.sub, fontSize: 12, fontWeight: 750, whiteSpace: "nowrap" }}>
                  {dense ? "넓게 보기" : "컴팩트"}
                </Button>
              </Stack>
            </Stack>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, borderTop: `1px solid ${UI.lineSoft}`, borderBottom: `1px solid ${UI.lineSoft}`, bgcolor: "#fafbfe", px: { xs: 1.1, md: 2.3 }, py: 0.9, flexWrap: "wrap" }}>
            <Stack direction="row" spacing={0.55} sx={{ overflowX: "auto", maxWidth: "100%", pb: 0.2, scrollbarWidth: "thin" }}>
              {viewTabs.map((tab) => {
                const selected = viewMode === tab.key;
                return <Button key={tab.key} onClick={() => handleViewModeChange(tab.key)} aria-pressed={selected}
                  sx={{ minWidth: "max-content", px: 1.6, py: 0.95, borderRadius: 1.7, whiteSpace: "nowrap", fontSize: 12.2, fontWeight: selected ? 850 : 700,
                    color: selected ? UI.blue : UI.sub, bgcolor: selected ? "#e9edff" : "transparent", "&:hover": { bgcolor: selected ? "#e2e8ff" : "#f0f3f8" } }}>{tab.label}</Button>;
              })}
            </Stack>
            <Typography sx={{ display: { xs: "none", xl: "block" }, flexShrink: 0, color: UI.muted, fontSize: 10.8, fontWeight: 650 }}>
              {viewDescriptions[viewMode]}
            </Typography>
          </Box>
          <Box sx={{ px: { xs: 1.8, md: 3 }, py: 1, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, bgcolor: "#fff" }}>
            <Typography sx={{ fontSize: 10.8, color: UI.muted }}>
              {hasSearched ? `현재 페이지 ${displayedRows.length}개 표시${resultKeyword ? ` · 결과 내 검색: ${resultKeyword}` : ""}` : "조건을 선택한 후 검색을 실행하세요."}
            </Typography>
            <Typography sx={{ fontSize: 10.8, fontWeight: 700, color: filtersDirty ? UI.amber : UI.muted }}>
              {loading ? "업데이트 중 · 이전 결과 유지" : filtersDirty ? "미적용 조건 있음" : "— 는 원천 데이터 없음"}
            </Typography>
          </Box>
          {loading ? <Box sx={{ height: 3, bgcolor: "#e8eefa", overflow: "hidden" }}><Box sx={{ height: "100%", width: "35%", bgcolor: UI.blue, animation: "pi-screener-load 1.2s infinite alternate" }} /></Box> : null}
          <ScreenerMobileResults rows={displayedRows} viewMode={viewMode} page={page} size={size} onOpen={(ticker, name) => handleGoStockIntelligence(ticker, name, "result-row")} />
          <ScreenerResultTable rows={displayedRows} viewMode={viewMode} page={page} size={size} dense={dense} loading={loading} hasSearched={hasSearched} onOpen={(ticker, name) => handleGoStockIntelligence(ticker, name, "result-row")} />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: { xs: "stretch", sm: "center" }, flexDirection: { xs: "column", sm: "row" }, gap: 1.3, px: { xs: 1.8, md: 3 }, py: 2, borderTop: `1px solid ${UI.lineSoft}`, bgcolor: "#fbfcfe" }}>
            <Typography sx={{ color: UI.sub, fontSize: 11.4, fontWeight: 700 }}>
              {hasSearched ? `${page + 1}페이지 · ${hasNext ? "다음 결과 있음" : "마지막 페이지"}` : "결과를 불러오면 페이지 이동을 사용할 수 있습니다."}
            </Typography>
            <Stack direction="row" spacing={0.8} alignItems="center" justifyContent={{ xs: "space-between", sm: "flex-end" }}>
              <FormControl size="small" sx={{ minWidth: 93 }}>
                <Select value={size} onChange={(event) => handleRowsPerPageChange(Number(event.target.value))} aria-label="페이지당 종목 개수"
                  sx={{ height: 39, borderRadius: 1.7, bgcolor: "#fff", fontSize: 11.5 }}>
                  {[30,50,100,200].map((n) => <MenuItem key={n} value={n}>{n}개씩</MenuItem>)}
                </Select>
              </FormControl>
              <Button variant="outlined" disabled={page <= 0 || loading} onClick={handlePrevPage} sx={{ height: 39, px: 1.8, borderRadius: 1.7, color: UI.text, borderColor: UI.line, fontSize: 11.8, fontWeight: 760 }}>이전</Button>
              <Button variant="contained" disabled={!hasNext || loading} onClick={handleNextPage} sx={{ height: 39, px: 1.8, borderRadius: 1.7, bgcolor: UI.navy, boxShadow: "none", fontSize: 11.8, fontWeight: 760, "&:hover": { bgcolor: "#293752", boxShadow: "none" } }}>다음</Button>
            </Stack>
          </Box>
        </Paper>
      </Stack>
      <style>{`@keyframes pi-screener-load { from { transform: translateX(-80%); } to { transform: translateX(340%); } } @media (prefers-reduced-motion: reduce) { .pi-screener-root * { animation: none !important; transition: none !important; } }`}</style>
    </Container>
  );
}
